import CurrencyInput from '../components/CurrencyInput';
import RecordedPayment from '../components/RecordedPayment';
import PaymentActions from '../components/PaymentActions';
import PayerSelect from '../components/PayerSelect';
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import JobDisposition from '../components/JobDisposition';
import PaymentArrangement from '../components/PaymentArrangement';
import { STAGE_COLORS } from '../constants/brand';
import { useAuth } from '../contexts/AuthContext';
const INSURANCE_ONLY_STAGES = ['Verify Claim Number/Checklist','Adjuster Meeting','Initial Insurance Estimate','Supplement','Appraisal','Full Approval'];

// Currency formatter - displays $X,XXX.XX format
const formatCurrency = (value: number | string | null | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : (value || 0);
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parse currency string back to number
const parseCurrency = (value: string): number => {
  return parseFloat(value.replace(/[$,]/g, '')) || 0;
};

interface Job {
  id: string;
  job_number: string | null;
  pipeline_stage_name: string;
  eagleview_report_url: string | null;
  commission_paid: boolean;
  job_type: string;
  total_price: number;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  estimate_id: string | null;
  homeowner_name: string;
  address: string;
  leads: {
    homeowner_name: string;
    address: string;
    homeowner_phone: string;
    homeowner_email: string;
    insurance_company: string;
    claim_number: string;
    lat: number;
    lng: number;
  };
  users: { name: string; email: string; phone: string; } | null;
  deposit_received?: boolean;
  deposit_amount?: number | null;
  deposit_method?: string | null;
  deposit_date?: string | null;
  final_payment_received?: boolean;
  final_payment_amount?: number | null;
  final_payment_method?: string | null;
  final_payment_date?: string | null;
}

interface Estimate {
  id: string;
  total_price: number;
  price_per_sf: number;
  total_sf: number;
  job_type: string;
  areas: any[];
  status: string;
  signed_at: string | null;
  payment_method: string | null;
  signature_image: string | null;
  salesman_commission: number;
  users: { name: string } | null;
}

interface Stage { id: number; name: string; order_index: number; auto_trigger: boolean; stage_type?: string; }
interface HistoryItem { id: string; stage_name: string; changed_at: string; notes: string; users: { name: string } | null; }

const TAGLINE = 'Licensed & Insured • Free Estimates • 3-Year Workmanship Warranty • 100% Satisfaction Guaranteed';
const LICENSES = 'DE License: DE-2026-000014249 • MD License: 167775 • PA License: T002819';
const WARRANT_TEXT = '3-Year Workmanship Warranty — McMullen Browne Development LLC warrants all workmanship for a period of 3 years from the date of completion.';
const JOB_DESCRIPTIONS: Record<string, string> = {
  asphalt: 'Professional asphalt paving installation including proper site preparation, grading, and compaction. Installation of hot-mix asphalt to specified depth with smooth finish. All work performed by experienced crews using professional-grade equipment.',
  concrete: 'Professional concrete installation including proper site preparation, sub-base compaction, and forming. Pouring and finishing of concrete to specifications with smooth, sealed surface. Includes control joints to prevent cracking.',
};

const formatScheduledDateTime = (scheduledDate?: string | null, scheduledTime?: string | null) => {
  if (!scheduledDate) return null;
  const formattedDate = new Date(`${scheduledDate}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const match = String(scheduledTime || '').match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return formattedDate;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = (hours % 12) || 12;
  return `${formattedDate} at ${hour12}:${String(minutes).padStart(2, '0')} ${suffix}`;
};

function generateEstimateHTML(estimate: Estimate, job: Job): string {
  const typeLabel = estimate.job_type?.charAt(0).toUpperCase() + estimate.job_type?.slice(1);
  const jobDesc = (estimate as any).notes?.trim() || JOB_DESCRIPTIONS[estimate.job_type] || JOB_DESCRIPTIONS.asphalt;
  const displayName = job.leads?.homeowner_name || job.homeowner_name;
  const displayAddress = job.leads?.address || job.address;
  const signedDate = estimate.signed_at ? new Date(estimate.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Estimate — ${displayName}</title>
  <style>
    @page { margin: 0; } * { margin:0;padding:0;box-sizing:border-box; } body { font-family:'Helvetica Neue',Arial,sans-serif;color:#1a1a1a;background:#fff; }
    .header { padding:60px 40px 6px;border-bottom:3px solid #4A7EB5; }
    .logo-area { display:flex;justify-content:space-between;align-items:center;margin-bottom:4px; }
    .estimate-badge { background:#4A7EB5;color:#fff;padding:8px 20px;border-radius:6px;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:1px; }
    .tagline { color:#666;font-size:11px;margin-top:4px;border-top:1px solid #eee;padding-top:4px; }
    .licenses { color:#4A7EB5;font-size:10px;margin-top:2px;font-weight:600; }
    .content { padding:40px; }
    .two-col { display:flex;gap:40px;margin-bottom:30px; } .col { flex:1; }
    .label { font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px; }
    .value { font-size:15px;color:#1a1a1a;font-weight:500; }
    .section-title { font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#4A7EB5;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #4A7EB5; }
    .scope { background:#f8f9fa;border-radius:8px;padding:20px;margin-bottom:30px;font-size:14px;line-height:1.7;color:#333; }
    .items-table { width:100%;border-collapse:collapse;margin-bottom:30px; }
    .items-table th { background:#0F1923;color:#fff;padding:12px 16px;text-align:left;font-size:12px;font-weight:700;text-transform:uppercase; }
    .items-table td { padding:14px 16px;border-bottom:1px solid #eee;font-size:14px; }
    .total-section { background:#0F1923;border-radius:10px;padding:24px 30px;margin-bottom:30px; }
    .total-row { display:flex;justify-content:space-between;align-items:center;margin-bottom:8px; }
    .total-label { color:rgba(255,255,255,0.7);font-size:14px; } .total-value { color:#fff;font-size:14px;font-weight:600; }
    .total-final { border-top:1px solid rgba(255,255,255,0.2);margin-top:12px;padding-top:12px; }
    .total-final .total-label { color:#fff;font-size:16px;font-weight:700; } .total-final .total-value { color:#4A7EB5;font-size:26px;font-weight:900; }
    .warranty { background:#e8f5e9;border:1px solid #10B981;border-radius:8px;padding:16px 20px;margin-bottom:30px;font-size:13px;color:#1b5e20; }
    .signature-section { border:2px dashed #ddd;border-radius:10px;padding:30px;margin-bottom:20px; }
    .sig-line { border-bottom:1px solid #333;margin:40px 0 8px; } .sig-label { font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.5px; }
    .footer { background:#f8f9fa;padding:20px 40px;text-align:center;font-size:11px;color:#888;border-top:1px solid #eee; }
    .deposit-note { background:#fff3cd;border:1px solid #ffc107;border-radius:8px;padding:14px 18px;margin-bottom:20px;font-size:13px;color:#856404; }
    .page2-wrapper { page-break-before:always;padding-top:60px; }
    .signed-banner { background:#10B981;color:#fff;padding:10px 20px;text-align:center;font-size:13px;font-weight:700; }
  </style></head><body>
  ${estimate.status === 'signed' ? `<div class="signed-banner">✅ SIGNED ESTIMATE — ${signedDate} — Payment: ${estimate.payment_method || 'N/A'}</div>` : ''}
  <div class="header">
    <div class="logo-area">
      <div>
        <div style="font-size:26px;font-weight:900;color:#0F1923;letter-spacing:-0.5px;line-height:1.1;">McMullen Browne</div>
        <div style="font-size:11px;font-weight:700;color:#4A7EB5;letter-spacing:2px;text-transform:uppercase;margin-top:2px;">Development LLC</div>
      </div>
      <div class="estimate-badge">Estimate</div>
    </div>
    <div class="tagline">${TAGLINE}</div>
    <div class="licenses">${LICENSES}</div>
  </div>
  <div class="content">
    <div class="two-col">
      <div class="col"><div class="label">Prepared For</div><div class="value" style="font-size:18px;font-weight:700;">${displayName}</div><div class="value" style="margin-top:4px;">${displayAddress}</div></div>
      <div class="col" style="text-align:right;"><div class="label">Date</div><div class="value">${new Date(estimate.signed_at || Date.now()).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div><div class="label" style="margin-top:12px;">Prepared By</div><div class="value">McMullen Browne Development LLC</div></div>
    </div>
    <div class="section-title">Scope of Work — ${typeLabel}</div>
    <div class="scope">${jobDesc}</div>
    <div class="section-title">Measurements & Pricing</div>
    <table class="items-table"><thead><tr><th>Area</th><th>Dimensions</th><th>Square Feet</th><th>Amount</th></tr></thead><tbody>
      ${(estimate.areas || []).map((a: any) => { const sf = (parseFloat(a.length)||0)*(parseFloat(a.width)||0); return `<tr><td>${a.name||'Area'}</td><td>${a.length} ft × ${a.width} ft</td><td>${sf.toFixed(0)} SF</td><td>$${(sf*estimate.price_per_sf).toFixed(2)}</td></tr>`; }).join('')}
    </tbody></table>
    <div class="total-section"><div class="total-row"><div class="total-label">Total Square Footage</div><div class="total-value">${estimate.total_sf?.toFixed(0)} SF</div></div><div class="total-row total-final"><div class="total-label">TOTAL ESTIMATE</div><div class="total-value">$${estimate.total_price?.toFixed(2)}</div></div></div>
    <div class="page2-wrapper">
      <div class="deposit-note">💳 <strong>20% Deposit Required to Schedule:</strong> $${(estimate.total_price*0.20).toFixed(2)} — Accepted: Cash, Check, or Stripe</div>
      <div class="warranty"><strong>✅ 3-Year Workmanship Warranty</strong><br>${WARRANT_TEXT}</div>
      <div class="signature-section">
        <div style="font-weight:700;font-size:16px;margin-bottom:20px;">Authorization to Proceed</div>
        <div style="font-size:13px;color:#555;margin-bottom:30px;">By signing below, I authorize McMullen Browne Development LLC to proceed with the work described above at the quoted price.</div>
        <div class="two-col">
          <div class="col">${estimate.signature_image ? `<img src="${estimate.signature_image}" style="height:70px;width:auto;max-width:280px;display:block;margin-bottom:4px;" />` : '<div class="sig-line"></div>'}<div class="sig-label">Homeowner Signature${signedDate ? ` — Signed ${signedDate}` : ''}</div></div>
          <div class="col"><div class="sig-line"></div><div class="sig-label">Date</div></div>
        </div>
      </div>
    </div>
  </div>
  <div class="footer">McMullen Browne Development LLC • mcmullenbrownecrm.com • Licensed & Insured<br/>DE: DE-2026-000014249 • MD: 167775 • PA: T002819</div>
  </body></html>`;
}

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const dispositionRequested = useLocation().search.includes("action=disposition");
  const [stages, setStages] = useState<Stage[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [pendingCommission, setPendingCommission] = useState<any>(null);
  const [checklist, setChecklist] = useState<any>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [fieldDoc, setFieldDoc] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docCategory, setDocCategory] = useState<string>('document');
  const [docDescription, setDocDescription] = useState<string>('');
  const [foremen, setForemen] = useState<any[]>([]);
  const [specialists, setSpecialists] = useState<any[]>([]);
  const [salesmen, setSalesmen] = useState<any[]>([]);
  const [assigningForeman, setAssigningForeman] = useState(false);
  const [assigningSpecialist, setAssigningSpecialist] = useState(false);
  const [assigningSalesman, setAssigningSalesman] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [financials, setFinancials] = useState<any>(null);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [jobCommissions, setJobCommissions] = useState<any[]>([]);
  const [uploadingInsurance, setUploadingInsurance] = useState(false);
  const [uploadingSupplement, setUploadingSupplement] = useState(false);
  const [uploadingEagleview, setUploadingEagleview] = useState(false);
  const [uploadingQxo, setUploadingQxo] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [generatingInvoicePdf, setGeneratingInvoicePdf] = useState(false);
  const [showAcvForm, setShowAcvForm] = useState(false);
  const [showDepreciationForm, setShowDepreciationForm] = useState(false);
  const [acvCheckNumber, setAcvCheckNumber] = useState('');
  const [acvAmount, setAcvAmount] = useState('');
  const [depreciationAmount, setDepreciationAmount] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [checkingCoverage, setCheckingCoverage] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ type: 'materials', description: '', amount: '' });
  const [dumpsterCount, setDumpsterCount] = useState<string | null>(null);
  const [savingDumpster, setSavingDumpster] = useState(false);
  const dumpsterSaveLock = useRef(false);
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('08:00');
  const [depositConfirmed, setDepositConfirmed] = useState<boolean | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [showReschedulePanel, setShowReschedulePanel] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('08:00');
  const [rescheduling, setRescheduling] = useState(false);
  const insuranceFileRef = React.useRef<HTMLInputElement>(null);
  const supplementFileRef = React.useRef<HTMLInputElement>(null);
  const eagleviewFileRef = React.useRef<HTMLInputElement>(null);
  const qxoFileRef = React.useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<string>('roof');
  // If job is an insurance job (no Roof tab), snap default to Insurance.
  useEffect(() => {
    if (job?.job_type === 'insurance' && activeTab === 'roof') {
      setActiveTab('insurance');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_type]);
  const [generatingWorkOrder, setGeneratingWorkOrder] = useState(false);
  const [workOrderUrl, setWorkOrderUrl] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethodInput, setPaymentMethodInput] = useState('cash');
  const [showBalanceForm, setShowBalanceForm] = useState(false);
  
  // Invoices & Payments state
  const [invoices, setInvoices] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [deletingPayment, setDeletingPayment] = useState<string | null>(null);
  const [creditMemos, setCreditMemos] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceTotal, setInvoiceTotal] = useState('');
  const [insuranceAmounts, setInsuranceAmounts] = useState({ acv: '', rcv: '', deductible: '' });
  const insuranceInvoiceFields = (values: any) => ({ acv: String(values?.acv ?? financials?.insurance_acv ?? ''), rcv: String(values?.rcv ?? financials?.insurance_rcv ?? ''), deductible: String(values?.deductible ?? financials?.insurance_deductible ?? '') });
  const invoiceClaimDetails = financials?.insurance_parse_details;
  const invoiceDetailsMatch = invoiceClaimDetails && ['rcv', 'acv', 'deductible'].every(key =>
    Math.abs(Number(insuranceAmounts[key as keyof typeof insuranceAmounts]) - Number(invoiceClaimDetails[key])) < 0.005);
  const invoiceDepreciation = invoiceDetailsMatch ? Number(invoiceClaimDetails.depreciation || 0)
    : Math.max(0, Number(insuranceAmounts.rcv) - Number(insuranceAmounts.acv));

  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [newInvoiceForm, setNewInvoiceForm] = useState({ invoice_type: 'custom', description: '', line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] });
  const [newPaymentForm, setNewPaymentForm] = useState({ payer_type: 'unknown', invoice_id: '', amount: '', payment_method: 'check', reference_number: '', internal_note: '' });
  
  const [paymentReceiptFile, setPaymentReceiptFile] = useState<File | null>(null);
  const [paymentAmountFocused, setPaymentAmountFocused] = useState(false);

  // Insurance Checks state
  const [insuranceChecks, setInsuranceChecks] = useState<any[]>([]);
  const [checksSummary, setChecksSummary] = useState<any>(null);
  const [showAddCheckModal, setShowAddCheckModal] = useState(false);
  const paymentCategoryTaken = (type: string) => ['acv', 'depreciation'].includes(type) && insuranceChecks.some((check: any) => check.check_type === type);
  const [newCheckForm, setNewCheckForm] = useState({ payment_method: 'check', payer_type: 'unknown', check_type: 'acv', check_number: '', amount: '', received_date: '', notes: '' });
  const [uploadingCheck, setUploadingCheck] = useState(false);
  const [checkSaveError, setCheckSaveError] = useState<string | null>(null);
  const checkImageRef = React.useRef<HTMLInputElement>(null);

  // Insurance ← Invoice sync state
  const [syncingFromInvoice, setSyncingFromInvoice] = useState(false);
  const [invoicePickerOpen, setInvoicePickerOpen] = useState(false);
  const [invoicePickerList, setInvoicePickerList] = useState<any[]>([]);
  const [openInvoiceMenu, setOpenInvoiceMenu] = useState<string | null>(null);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceMethod, setBalanceMethod] = useState('cash');
  const [recordingBalance, setRecordingBalance] = useState(false);

  // Notes & Tasks state
  const [jobNotes, setJobNotes] = useState<any[]>([]);
  const [jobTasks, setJobTasks] = useState<any[]>([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [allUsers, setAllUsers] = useState<any[]>([]);
  
  // Mention picker state
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const noteInputRef = React.useRef<HTMLTextAreaElement>(null);
  
  // Task creation modal
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [newTaskDue, setNewTaskDue] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('normal');
  const [newTaskType, setNewTaskType] = useState('follow_up');
  const [newTaskJobId, setNewTaskJobId] = useState<string | null>(null);
  const [creatingTask, setCreatingTask] = useState(false);
  const [financialError, setFinancialError] = useState('');

  useEffect(() => { fetchAll(); }, [id]);

  // Close invoice menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenInvoiceMenu(null);
    if (openInvoiceMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openInvoiceMenu]);

  const fetchAll = async () => {
    setFinancialError('');
    try {
      const [jobRes, stagesRes, historyRes, commissionsRes] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get('/pipeline/stages'),
        api.get(`/pipeline/history/${id}`),
        api.get('/commissions/pending').catch(() => ({ data: [] })),
      ]);
      const jobData = jobRes.data;
      setJob(jobData);
      // Filter stages by job type — each division gets its own stage list
      const isAC = ['asphalt', 'concrete'].includes(jobData?.job_type);
      const isInsurance = jobData?.job_type === 'insurance';
      const allStages: Stage[] = stagesRes.data;
      const filtered = isInsurance
        ? allStages.filter((s: Stage) => s.stage_type === 'insurance')
        : isAC
          ? allStages.filter((s: Stage) => s.stage_type === 'ac')
          : allStages.filter((s: Stage) => s.stage_type === 'roofing' || !s.stage_type);
      setStages(filtered);
      setHistory(historyRes.data);
      const pc = commissionsRes.data.find((c: any) => c.job_id === id);
      setPendingCommission(pc || null);

      // Load users for assignment dropdowns
      api.get('/users').then(r => {
        const users = r.data || [];
        setForemen(users.filter((u: any) => u.role === 'foreman'));
        setSpecialists(users.filter((u: any) => u.role === 'specialist' || u.role === 'pm'));
        setSalesmen(users.filter((u: any) => u.role === 'salesman' || u.role === 'estimator'));
      }).catch(() => {});
      // Load field documentation
      api.get(`/documentation/jobs/${id}`).then(r => setFieldDoc(r.data?.[0] || null)).catch(() => {});
      // Load job documents
      api.get(`/jobs/${id}/documents`).then(r => setDocuments(r.data || [])).catch(() => {});

      // Load checklist (roofing) or estimate (AC)
      const jobIsAC = ['asphalt', 'concrete'].includes(jobData?.job_type);
      if (jobIsAC && jobData?.estimate_id) {
        api.get(`/estimates/${jobData.estimate_id}`).then(r => setEstimate(r.data)).catch(() => {});
      } else if (!jobIsAC) {
        const leadId = jobData?.lead_id;
        if (leadId) api.get(`/leads/${leadId}/checklist`).then(r => setChecklist(r.data)).catch(() => {});
        // Load roofing financials
        api.get(`/financials/${id}`).then(r => {
          setFinancials(r.data.financials);
          const loadedExpenses = r.data.expenses || [];
          setExpenses(loadedExpenses);
        }).catch(() => setFinancialError('Financial information could not be loaded. Refresh before reviewing totals or adding expenses.'));
        // Load commissions for this job
        api.get(`/commissions?job_id=${id}`).then(r => {
          setJobCommissions(r.data || []);
        }).catch(() => {});
        // Load insurance checks
        api.get(`/jobs/${id}/insurance-checks/summary`).then(r => {
          setChecksSummary(r.data);
          setInsuranceChecks(r.data?.checks || []);
        }).catch(() => {});
        // Load invoices & payments
        loadInvoicesAndPayments();
      }
      
      // Load notes & tasks for all job types
      api.get(`/notes/job/${id}`).then(r => setJobNotes(r.data || [])).catch(() => {});
      api.get(`/tasks?job_id=${id}`).then(r => setJobTasks(r.data || [])).catch(() => {});
      api.get('/users').then(r => setAllUsers(r.data || [])).catch(() => {});
    } catch (e: any) { 
      console.error('JobDetailPage fetchAll error:', e); 
      setLoadError(e?.response?.data?.error || e?.message || 'Failed to load job');
    }
  };

  const loadInvoicesAndPayments = async () => {
    if (!id) return;
    setLoadingInvoices(true);
    try {
      const [invRes, payRes, cmRes, finRes, checksRes] = await Promise.all([
        api.get(`/roofing-invoices/job/${id}`),
        api.get(`/roofing-invoices/payments/job/${id}`),
        api.get(`/roofing-invoices/credit-memos/job/${id}`),
        api.get(`/financials/${id}`),
        api.get(`/jobs/${id}/insurance-checks/summary`),
      ]);
      setInvoices(invRes.data || []);
      setPayments(payRes.data || []);
      setCreditMemos(cmRes.data || []);
      setFinancials(finRes.data.financials);
      setChecksSummary(checksRes.data);
      setInsuranceChecks(checksRes.data?.checks || []);
    } catch (e) { console.error('Error loading invoices:', e); }
    finally { setLoadingInvoices(false); }
  };

  const deletePayment = async (payment: any) => {
    const linked = Boolean(payment.source_check_id);
    const message = `Delete payment ${payment.payment_number} for $${Number(payment.amount).toFixed(2)}?` +
      (linked ? ' This also deletes its linked insurance check record and image.' : '') +
      ' Invoice totals will be updated. This cannot be undone.';
    if (!window.confirm(message)) return;
    setDeletingPayment(payment.id);
    try {
      const { data } = await api.delete(linked
        ? `/jobs/${id}/insurance-checks/${payment.source_check_id}`
        : `/roofing-invoices/payments/${payment.id}`);
      await loadInvoicesAndPayments();
      if (data.sync_warning) alert(data.sync_warning);
    } catch (e: any) {
      alert('Failed to delete payment: ' + (e.response?.data?.error || e.message));
    } finally {
      setDeletingPayment(null);
    }
  };

  const openEdit = () => {
    if (!job) return;
    const fullName = job.leads?.homeowner_name || job.homeowner_name || '';
    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    setEditForm({
      first_name: firstName,
      last_name: lastName,
      address: job.leads?.address || job.address || '',
      homeowner_phone: job.leads?.homeowner_phone || '',
      homeowner_email: job.leads?.homeowner_email || '',
      insurance_company: job.leads?.insurance_company || '',
      claim_number: job.leads?.claim_number || '',
    });
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editForm.first_name?.trim()) return alert('First name is required');
    if (!editForm.last_name?.trim()) return alert('Last name is required');
    const homeowner_name = `${editForm.first_name.trim()} ${editForm.last_name.trim()}`;
    setSaving(true);
    try {
      await api.patch(`/jobs/${id}`, {
        homeowner_name,
        address: editForm.address,
      });
      // Update lead fields if lead exists
      const leadId = (job as any).lead_id;
      if (leadId) {
        await api.patch(`/leads/${leadId}`, {
          homeowner_name,
          address: editForm.address,
          homeowner_phone: editForm.homeowner_phone,
          homeowner_email: editForm.homeowner_email,
          insurance_company: editForm.insurance_company,
          claim_number: editForm.claim_number,
        });
      }
      setShowEditModal(false);
      fetchAll();
    } catch (e) { alert('Failed to save changes'); }
    finally { setSaving(false); }
  };

  const deleteJob = async () => {
    if (!window.confirm(`Delete job for ${displayName}?\n\nThis will permanently delete the job, all stage history, and commissions. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/jobs/${id}`);
      navigate('/pipeline');
    } catch (e) { alert('Failed to delete job'); }
    finally { setDeleting(false); }
  };

  const uploadInsuranceEstimate = async (file: File, isSupp = false) => {
    const setter = isSupp ? setUploadingSupplement : setUploadingInsurance;
    const endpoint = isSupp ? 'upload-supplement' : 'upload-insurance';
    setter(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/financials/${id}/${endpoint}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.reviewRequired) {
        setDocuments(prev => [data.document, ...prev]);
        alert(`Estimate saved in Docs — needs review.\n\n${data.parseError}\n\nClaim amounts, invoices and payments were not changed. Review the PDF and enter the confirmed claim amounts in Insurance.`);
        return;
      }
      if (data.document) setDocuments(prev => [data.document, ...prev]);
      setFinancials(data.financials);
      // Reload invoices if a new invoice was created
      if (data.invoice) loadInvoicesAndPayments();
      if (data.squareMismatch) alert(data.squareMismatch);
      if (data.invoiceWarning) { alert(data.invoiceWarning); return; }
      if (data.parseError) alert(`⚠️ PDF parsed but some values may need review: ${data.parseError}`);
      else alert(`✅ ${isSupp ? 'Supplement' : 'Insurance'} estimate parsed successfully!${data.parsed?.squares > 0 ? `\n\n📏 Squares extracted: ${data.parsed.squares}` : ''}${data.invoice ? '\n\n🧾 Invoice created or updated.' : ''}`);
    } catch (e: any) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
    finally {
      setter(false);
      const input = isSupp ? supplementFileRef.current : insuranceFileRef.current;
      if (input) input.value = '';
    }
  };

  const ROOFING_PAYMENT_STAGES = ['Job Complete \u2014 Completion Pics & COC', 'Material Order', 'Final Invoice', 'Pending Payment', 'Paid and Closed'];

  const createRoofingInvoice = async () => {
    if (!window.confirm('Create QB invoice for full RCV and send deductible payment link to homeowner?')) return;
    setCreatingInvoice(true);
    try {
      await api.post(`/roofing/jobs/${id}/invoice/create`);
      alert('\u2705 QB invoice created and deductible link sent to homeowner.');
      fetchAll();
    } catch (e: any) { alert('Failed to create invoice: ' + (e.response?.data?.error || e.message)); }
    finally { setCreatingInvoice(false); }
  };

  const recordAcv = async () => {
    if (!acvAmount || parseFloat(acvAmount) <= 0) return alert('Enter a valid deposit amount.');
    setRecordingPayment(true);
    try {
      await api.post(`/roofing/jobs/${id}/payments/acv`, {
        check_number: acvCheckNumber,
        amount: parseFloat(acvAmount),
        received_date: new Date().toISOString(),
      });
      alert('\u2705 Deposit recorded.');
      setShowAcvForm(false);
      setAcvCheckNumber('');
      setAcvAmount('');
      fetchAll();
    } catch (e: any) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setRecordingPayment(false); }
  };

  const recordDepreciation = async () => {
    if (!depreciationAmount || parseFloat(depreciationAmount) <= 0) return alert('Enter a valid final payment amount.');
    setRecordingPayment(true);
    try {
      await api.post(`/roofing/jobs/${id}/payments/depreciation`, {
        amount: parseFloat(depreciationAmount),
        received_date: new Date().toISOString(),
      });
      alert('\u2705 Final Payment recorded.');
      setShowDepreciationForm(false);
      setDepreciationAmount('');
      fetchAll();
    } catch (e: any) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setRecordingPayment(false); }
  };

  const checkCoverage = async () => {
    setCheckingCoverage(true);
    try {
      const { data } = await api.post(`/financials/${id}/check-coverage`);
      setFinancials((prev: any) => prev ? { ...prev, coverage_status: data.status, coverage_gap: data.gap, coverage_notes: data.notes } : prev);
    } catch (e) { alert('Coverage check failed'); }
    finally { setCheckingCoverage(false); }
  };

  // Insurance Checks functions
  const addInsuranceCheck = async (checkImageFile?: File) => {
    if (paymentCategoryTaken(newCheckForm.check_type)) { alert('That payment category is already recorded. Edit the existing payment instead.'); return; }
    if (newCheckForm.payer_type === 'unknown') { alert('Choose the payer.'); return; }
    if (!newCheckForm.check_type || !newCheckForm.amount) {
      alert('Please fill in check type and amount');
      return;
    }
    if (uploadingCheck) return;
    setCheckSaveError(null);
    if (checkImageFile && checkImageFile.size > 10 * 1024 * 1024) {
      setCheckSaveError('Receipt must be 10 MB or smaller. No payment was recorded.');
      return;
    }
    setUploadingCheck(true);
    try {
      const formData = new FormData();
      formData.append('check_type', newCheckForm.check_type);
      formData.append('payer_type', newCheckForm.payer_type);
      formData.append('payment_method', newCheckForm.payment_method);
      formData.append('amount', newCheckForm.amount);
      if (newCheckForm.check_number) formData.append('check_number', newCheckForm.check_number);
      if (newCheckForm.received_date) formData.append('received_date', newCheckForm.received_date);
      if (newCheckForm.notes) formData.append('notes', newCheckForm.notes);
      if (checkImageFile) formData.append('check_image', checkImageFile);

      const { data } = await api.post(`/jobs/${id}/insurance-checks`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setInsuranceChecks(prev => [data, ...prev]);
      await loadInvoicesAndPayments();
      
      setShowAddCheckModal(false);
      setNewCheckForm({ payment_method: 'check', payer_type: 'unknown', check_type: 'acv', check_number: '', amount: '', received_date: '', notes: '' });
      alert(data.sync_warning || `✅ ${newCheckForm.check_type === 'acv' ? 'Deposit' : newCheckForm.check_type === 'depreciation' ? 'Final Payment' : 'Payment'} recorded!`);
    } catch (e: any) {
      setCheckSaveError(e.response?.data?.error || 'Could not confirm whether the payment saved. Refresh the payment list before trying again to avoid a duplicate.');
    } finally {
      setUploadingCheck(false);
    }
  };

  const deleteInsuranceCheck = async (checkId: string) => {
    if (!window.confirm('Delete this check record?')) return;
    try {
      const { data } = await api.delete(`/jobs/${id}/insurance-checks/${checkId}`);
      if (data.sync_warning) alert(data.sync_warning);
      setInsuranceChecks(prev => prev.filter(c => c.id !== checkId));
      await loadInvoicesAndPayments();
    } catch (e: any) {
      alert('Failed to delete: ' + (e.response?.data?.error || e.message));
    }
  };

  const generateInvoicePdf = async () => {
    if (!window.confirm('Generate invoice PDF from insurance estimate data?')) return;
    setGeneratingInvoicePdf(true);
    try {
      const { data } = await api.post(`/financials/${id}/generate-invoice`);
      setFinancials((prev: any) => prev ? { ...prev, ...data.financials } : data.financials);
      alert('✅ Invoice PDF generated!');
    } catch (e: any) {
      alert('Failed to generate invoice: ' + (e.response?.data?.error || e.message));
    } finally {
      setGeneratingInvoicePdf(false);
    }
  };

  const addExpense = async () => {
    if (!expenseForm.amount || !expenseForm.type) return;
    try {
      const { data } = await api.post(`/financials/${id}/expenses`, expenseForm);
      setExpenses(prev => [...prev, data]);
      setExpenseForm({ type: 'materials', description: '', amount: '' });
      setShowAddExpense(false);
    } catch (e: any) { alert('Failed to add expense'); }
  };

  const deleteExpense = async (expenseId: string) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/financials/${id}/expenses/${expenseId}`);
      setExpenses(prev => prev.filter(e => e.id !== expenseId));
    } catch (e) { alert('Failed to delete'); }
  };

  const getCurrentIdx = () => stages.findIndex(s => s.name === job?.pipeline_stage_name);
  const getNextStage = () => { if (['Lost','Closed'].includes(job?.pipeline_stage_name || '') || (!['asphalt','concrete'].includes(job?.job_type || '') && job?.pipeline_stage_name === 'Paid and Closed')) return null; const i = getCurrentIdx(); return stages.slice(i + 1).find(s => s.name !== 'Lost' && (financials?.payment_arrangement !== 'retail' || !INSURANCE_ONLY_STAGES.includes(s.name))) || null; };

  const uploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', docCategory);
      if (docDescription.trim()) formData.append('description', docDescription.trim());
      const { data } = await api.post(`/jobs/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocuments(prev => [data, ...prev]);
      setDocDescription('');
    } catch (e) { alert('Upload failed'); }
    finally { setUploadingDoc(false); e.target.value = ''; }
  };

  const deleteDocument = async (docId: string) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/jobs/${id}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (e) { alert('Delete failed'); }
  };

  const uploadEagleviewReport = async (file: File) => {
    setUploadingEagleview(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploadRes } = await api.post(`/jobs/${id}/materials/eagleview`, formData);
      
      // Update job data
      const { data: jobData } = await api.get(`/jobs/${id}`);
      setJob(jobData);
      
      // Update financials with parsed measurements
      if (uploadRes.financials) {
        setFinancials(uploadRes.financials);
      }
      
      const docs = await api.get(`/jobs/${id}/documents`);
      setDocuments(docs.data || []);
      
      // Show parse result
      if (uploadRes.parsed?.total_squares) {
        alert(`✅ ${uploadRes.parsed.report_type === 'hover' ? 'HOVER' : 'EagleView'} parsed!\n\nSquares: ${uploadRes.parsed.total_squares}\nPitch: ${uploadRes.parsed.predominant_pitch || 'N/A'}\nFacets: ${uploadRes.parsed.facets || 'N/A'}\n\nYou can edit these values if needed.`);
      } else if (uploadRes.parseError) {
        alert(`⚠️ PDF uploaded but parsing failed: ${uploadRes.parseError}\n\nPlease enter measurements manually.`);
      }
    } catch (e: any) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUploadingEagleview(false); if (eagleviewFileRef.current) eagleviewFileRef.current.value = ''; }
  };

  const reparseRoofReport = async () => {
    setUploadingEagleview(true);
    try {
      const { data } = await api.post(`/jobs/${id}/materials/roof-report/reparse`);
      setFinancials(data.financials);
      alert(`✅ ${data.parsed.report_type === 'hover' ? 'HOVER' : 'EagleView'} measurements recalculated from the stored PDF.`);
    } catch (e: any) { alert('Recalculation failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUploadingEagleview(false); }
  };

  const uploadQxoOrder = async (file: File) => {
    setUploadingQxo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploadRes } = await api.post(`/jobs/${id}/materials/qxo`, formData);
      
      // Update job data
      const { data: jobData } = await api.get(`/jobs/${id}`);
      setJob(jobData);
      
      // Update financials with parsed material order data
      if (uploadRes.financials) {
        setFinancials(uploadRes.financials);
      }
      
      const docs = await api.get(`/jobs/${id}/documents`);
      setDocuments(docs.data || []);
      
      // Show parse result
      if (uploadRes.parsed?.total) {
        alert(`✅ Material order parsed!\n\nSupplier: ${uploadRes.parsed.supplier || 'Unknown'}\nTotal: $${uploadRes.parsed.total.toLocaleString()}\n\nYou can edit these values if needed.`);
      } else if (uploadRes.parseError) {
        alert(`⚠️ PDF uploaded but parsing failed: ${uploadRes.parseError}\n\nPlease enter the total manually.`);
      }
    } catch (e: any) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUploadingQxo(false); }
  };

  const generateWorkOrder = async () => {
    setGeneratingWorkOrder(true);
    try {
      const { data } = await api.post(`/jobs/${id}/work-order/generate`);
      setWorkOrderUrl(data.url);
      const docs = await api.get(`/jobs/${id}/documents`);
      setDocuments(docs.data || []);
    } catch (e) { alert('Failed to generate work order'); }
    finally { setGeneratingWorkOrder(false); }
  };

  const getFileIcon = (type: string) => {
    if (['jpg','jpeg','png','gif','webp'].includes(type)) return '🖼️';
    if (type === 'pdf') return '📄';
    if (['doc','docx'].includes(type)) return '📝';
    if (['xls','xlsx','csv'].includes(type)) return '📊';
    return '📎';
  };

  const advanceStage = async () => {
    const next = getNextStage();
    if (!next || !window.confirm(`Advance to "${next.name}"?${next.auto_trigger ? '\n\n⚡ This will trigger an automatic action.' : ''}`)) return;
    setAdvancing(true);
    try {
      await api.post(`/jobs/${id}/stage`, { stage_name: next.name });
      fetchAll();
    } catch (e) { alert('Failed to advance stage'); }
    finally { setAdvancing(false); }
  };

  const approveCommission = async () => {
    if (!pendingCommission || !window.confirm(`Approve $${pendingCommission.amount} commission for ${pendingCommission.users?.name}?`)) return;
    setApproving(true);
    try {
      await api.post(`/commissions/${pendingCommission.id}/approve`);
      setPendingCommission(null);
      fetchAll();
      alert('✅ Commission approved.');
    } catch (e) { alert('Failed to approve'); }
    finally { setApproving(false); }
  };

  if (loadError) return <div style={{ padding: 32, color: '#E74C3C' }}>Error: {loadError} <button onClick={() => { setLoadError(null); fetchAll(); }} style={{ marginLeft: 12, padding: '6px 12px', background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Retry</button></div>;
  if (!job) return <div style={{ padding: 32, color: '#8FA8C0' }}>Loading...</div>;

  const isAC = ['asphalt', 'concrete'].includes(job.job_type);
  const isInsurance = job.job_type === 'insurance';
  const isRoofing = job.job_type === 'roofing';
  const currentIdx = getCurrentIdx();
  const nextStage = getNextStage();
  const activeStageCount = stages.filter(s => s.name !== 'Lost').length;
  const isLost = job.pipeline_stage_name === 'Lost';
  const progress = !isLost && activeStageCount > 0 ? ((currentIdx + 1) / activeStageCount * 100) : 0;
  const stageColor = STAGE_COLORS[job.pipeline_stage_name] || '#4A7EB5';
  const displayName = job.leads?.homeowner_name || job.homeowner_name;
  const displayAddress = job.leads?.address || job.address;
  const jobTypeLabel = job.job_type?.charAt(0).toUpperCase() + job.job_type?.slice(1);
  const jobTypePillColor = isAC ? '#F59E0B' : isInsurance ? '#06B6D4' : '#4A7EB5';
  const formattedScheduledAt = formatScheduledDateTime(job.scheduled_date, job.scheduled_time);

  const dumpsterExpenses = expenses.filter((expense: any) => expense.type === 'dumpster');
  const recommendedDumpsters = Number(financials?.squares || 0) > 45 ? 2 : 1;
  const recordedDumpsters = dumpsterExpenses.reduce((total: number, expense: any) => total + (Number(expense.quantity) || 1), 0);
  const dumpsterQuantity = dumpsterCount ?? String(dumpsterExpenses.length ? recordedDumpsters : recommendedDumpsters);


  return (
    <>
    <div style={{ padding: 32, maxWidth: 1400 }}>
      {financialError && <div role="alert" style={{ background: '#78350F', color: '#FEF3C7', padding: 16, borderRadius: 8, marginBottom: 16 }}>{financialError}</div>}
      {/* Back */}
      <button onClick={() => navigate('/pipeline')} style={{ background: 'none', color: '#8FA8C0', fontSize: 14, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        ← Back to Pipeline
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24 }}>
        {/* Left column */}
        <div>
          {/* Job header */}
          <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0 }}>{displayName}</h1>
                {job.job_number && <span style={{ background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', borderRadius: 8, padding: '4px 12px', fontSize: 13, fontWeight: 700 }}>{job.job_number}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: `${jobTypePillColor}22`, color: jobTypePillColor, border: `1px solid ${jobTypePillColor}44`, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700 }}>{jobTypeLabel}</span>
                {(user?.role === 'admin' || user?.role === 'specialist') && (
                  <button onClick={openEdit} style={{ background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>✏️ Edit</button>
                )}
              </div>
            </div>
            <p style={{ color: '#8FA8C0', marginBottom: 16 }}>📍 {displayAddress}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {job.leads?.homeowner_phone && <div><span style={{ color: '#4A6580', fontSize: 11 }}>PHONE</span><br /><a href={`tel:${job.leads.homeowner_phone}`} style={{ color: '#4A7EB5', fontWeight: 600 }}>{job.leads.homeowner_phone}</a></div>}
              {job.leads?.homeowner_email && <div><span style={{ color: '#4A6580', fontSize: 11 }}>EMAIL</span><br /><a href={`mailto:${job.leads.homeowner_email}`} style={{ color: '#4A7EB5', fontWeight: 600 }}>{job.leads.homeowner_email}</a></div>}
              {isAC && job.total_price && <div><span style={{ color: '#4A6580', fontSize: 11 }}>CONTRACT VALUE</span><br /><span style={{ color: '#10B981', fontWeight: 800, fontSize: 20 }}>${job.total_price?.toLocaleString()}</span></div>}
              {isAC && job.total_price && <div><span style={{ color: '#4A6580', fontSize: 11 }}>20% DEPOSIT</span><br /><span style={{ color: '#F59E0B', fontWeight: 700, fontSize: 16 }}>${(job.total_price * 0.20).toFixed(2)}</span></div>}
              {formattedScheduledAt && <div><span style={{ color: '#4A6580', fontSize: 11 }}>SCHEDULED</span><br /><span style={{ color: '#4A7EB5', fontWeight: 700 }}>{formattedScheduledAt}</span></div>}
              {/* Assignment Dropdowns Row */}
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {/* Specialist */}
                <div>
                  <span style={{ color: '#4A6580', fontSize: 11 }}>SPECIALIST</span><br />
                  <select
                    value={(job as any).assigned_specialist_id || ''}
                    onChange={async (e) => {
                      setAssigningSpecialist(true);
                      await api.patch(`/jobs/${id}`, { assigned_specialist_id: e.target.value || null });
                      fetchAll();
                      setAssigningSpecialist(false);
                    }}
                    style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, marginTop: 4, cursor: 'pointer', width: '100%' }}
                  >
                    <option value="">— Unassigned —</option>
                    {specialists.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {assigningSpecialist && <span style={{ color: '#8FA8C0', fontSize: 11 }}>Saving...</span>}
                </div>
                {/* Salesman */}
                <div>
                  <span style={{ color: '#4A6580', fontSize: 11 }}>SALESMAN</span><br />
                  <select
                    value={(job as any).assigned_salesman_id || ''}
                    onChange={async (e) => {
                      setAssigningSalesman(true);
                      await api.patch(`/jobs/${id}`, { assigned_salesman_id: e.target.value || null });
                      fetchAll();
                      setAssigningSalesman(false);
                    }}
                    style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, marginTop: 4, cursor: 'pointer', width: '100%' }}
                  >
                    <option value="">— Unassigned —</option>
                    {salesmen.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {assigningSalesman && <span style={{ color: '#8FA8C0', fontSize: 11 }}>Saving...</span>}
                </div>
                {/* Foreman */}
                <div>
                  <span style={{ color: '#4A6580', fontSize: 11 }}>FOREMAN</span><br />
                  <select
                    value={(job as any).assigned_foreman_id || ''}
                    onChange={async (e) => {
                      setAssigningForeman(true);
                      await api.patch(`/jobs/${id}`, { assigned_foreman_id: e.target.value || null });
                      fetchAll();
                      setAssigningForeman(false);
                    }}
                    style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, marginTop: 4, cursor: 'pointer', width: '100%' }}
                  >
                    <option value="">— Unassigned —</option>
                    {foremen.map((f: any) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  {assigningForeman && <span style={{ color: '#8FA8C0', fontSize: 11 }}>Saving...</span>}
                </div>
              </div>
              {!isAC && job.leads?.insurance_company && <div><span style={{ color: '#4A6580', fontSize: 11 }}>INSURANCE</span><br /><span style={{ color: '#fff' }}>{job.leads.insurance_company}</span></div>}
              {!isAC && job.leads?.claim_number && <div><span style={{ color: '#4A6580', fontSize: 11 }}>CLAIM #</span><br /><span style={{ color: '#4A7EB5', fontWeight: 700, fontSize: 16 }}>{job.leads.claim_number}</span></div>}
              {isRoofing && job.eagleview_report_url && <div><span style={{ color: '#4A6580', fontSize: 11 }}>ROOF REPORT</span><br /><a href={job.eagleview_report_url} target="_blank" rel="noreferrer" style={{ color: '#10B981', fontWeight: 600 }}>📊 View Report</a></div>}
            </div>
          </div>

          {/* ─── TAB NAVIGATION (roofing only) ─── */}
          {!isAC && (() => {
            const TABS = [
              // Roof tab is roofing-only; insurance jobs don't need EagleView / roof checklist
              ...(isRoofing ? [{ id: 'roof', label: '🏠 Roof' }] : []),
              { id: 'insurance',  label: '🏦 Insurance' },
              { id: 'work_materials', label: '🛠️ Work' },
              { id: 'contract',   label: '✍️ Contract' },
              { id: 'notes',      label: '💬 Notes' },
              { id: 'invoices',   label: '🧾 Invoices' },
              { id: 'documents',  label: '📁 Docs' },
              { id: 'financials', label: '💰 Financials' },
              { id: 'history',    label: '🕒 History' },
            ];
            return (
              <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#111D27', borderRadius: 12, padding: 4, flexWrap: 'nowrap' }}>
                {TABS.map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                    flex: '1 1 auto', minWidth: 0, padding: '9px 8px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s', whiteSpace: 'nowrap',
                    background: activeTab === t.id ? '#4A7EB5' : 'transparent',
                    color: activeTab === t.id ? '#fff' : '#4A6580',
                  }}>{t.label}</button>
                ))}
              </div>
            );
          })()}

          {/* Reschedule — shown for AC jobs already scheduled */}
          {isAC && job.scheduled_date && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #F59E0B44', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showReschedulePanel ? 16 : 0 }}>
                <div>
                  <div style={{ color: '#F59E0B', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>📅 Reschedule Job</div>
                  <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 4 }}>Currently: <strong style={{ color: '#fff' }}>{formattedScheduledAt}</strong></div>
                </div>
                <button
                  onClick={() => { setShowReschedulePanel(p => !p); setRescheduleDate(job.scheduled_date || ''); setRescheduleTime(job.scheduled_time || '08:00'); }}
                  style={{ background: '#F59E0B22', color: '#F59E0B', border: '1px solid #F59E0B44', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  {showReschedulePanel ? 'Cancel' : 'Reschedule'}
                </button>
              </div>
              {showReschedulePanel && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>New Date</label>
                      <input
                        type="date"
                        value={rescheduleDate}
                        onChange={e => setRescheduleDate(e.target.value)}
                        style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>New Time</label>
                      <input
                        type="time"
                        value={rescheduleTime}
                        onChange={e => setRescheduleTime(e.target.value)}
                        style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}
                      />
                    </div>
                  </div>
                  <button
                    disabled={!rescheduleDate || rescheduling}
                    onClick={async () => {
                      if (!rescheduleDate) return;
                      setRescheduling(true);
                      try {
                        await api.patch(`/jobs/${id}/schedule`, { scheduled_date: rescheduleDate, scheduled_time: rescheduleTime });
                        setShowReschedulePanel(false);
                        fetchAll();
                        alert(`✅ Job rescheduled to ${new Date(rescheduleDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}. Calendar updated and homeowner notified.`);
                      } catch (e: any) {
                        alert('Failed to reschedule: ' + (e?.response?.data?.error || e.message));
                      } finally {
                        setRescheduling(false);
                      }
                    }}
                    style={{ width: '100%', background: !rescheduleDate || rescheduling ? '#2D4259' : '#F59E0B', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 700, cursor: !rescheduleDate || rescheduling ? 'default' : 'pointer' }}
                  >
                    {rescheduling ? 'Rescheduling...' : '📅 Confirm Reschedule & Notify Homeowner'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Schedule Job — shown for AC jobs not yet scheduled */}
          {isAC && !job.scheduled_date && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #4A7EB544', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showSchedulePanel ? 16 : 0 }}>
                <div>
                  <div style={{ color: '#4A7EB5', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>🗓️ Schedule This Job</div>
                  <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 4 }}>Job is at <strong style={{ color: '#F59E0B' }}>{job.pipeline_stage_name}</strong> — pick a date to schedule.</div>
                </div>
                <button
                  onClick={() => setShowSchedulePanel(p => !p)}
                  style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  {showSchedulePanel ? 'Cancel' : 'Schedule Job'}
                </button>
              </div>
              {showSchedulePanel && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Start Date</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={e => setScheduleDate(e.target.value)}
                        style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Start Time</label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={e => setScheduleTime(e.target.value)}
                        style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}
                      />
                    </div>
                  </div>
                  {estimate?.payment_method && !['cash', 'check'].includes(estimate.payment_method.toLowerCase()) ? (
                    <div style={{ background: '#10B98122', border: '1px solid #10B98144', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#10B981' }}>
                      💳 Online payment — QB deposit link already sent to homeowner.
                    </div>
                  ) : (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 8 }}>Was the 20% deposit collected?</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[true, false].map(v => (
                          <button
                            key={String(v)}
                            onClick={() => setDepositConfirmed(v)}
                            style={{ flex: 1, padding: '8px', borderRadius: 8, border: `1px solid ${depositConfirmed === v ? '#10B981' : '#2D4259'}`, background: depositConfirmed === v ? '#10B98122' : '#243447', color: depositConfirmed === v ? '#10B981' : '#8FA8C0', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                          >
                            {v ? '✅ Yes, collected' : '⏳ Not yet'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    disabled={!scheduleDate || scheduling}
                    onClick={async () => {
                      if (!scheduleDate) return;
                      setScheduling(true);
                      try {
                        await api.post(`/jobs/${id}/schedule`, {
                          scheduled_date: scheduleDate,
                          scheduled_time: scheduleTime,
                          deposit_collected: depositConfirmed ?? false,
                        });
                        setShowSchedulePanel(false);
                        fetchAll();
                        alert(`✅ Job scheduled for ${new Date(scheduleDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}. Google Calendar event created and homeowner notified.`);
                      } catch (e: any) {
                        alert('Failed to schedule: ' + (e?.response?.data?.error || e.message));
                      } finally {
                        setScheduling(false);
                      }
                    }}
                    style={{ width: '100%', background: !scheduleDate || scheduling ? '#2D4259' : '#10B981', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 700, cursor: !scheduleDate || scheduling ? 'default' : 'pointer' }}
                  >
                    {scheduling ? 'Scheduling...' : '📅 Confirm & Schedule Job'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Commission Approval — always visible */}
          {pendingCommission && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #F59E0B44', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#F59E0B', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Pending Commission</div>
                  <div style={{ color: '#fff', fontSize: 20, fontWeight: 800, marginTop: 4 }}>${pendingCommission.amount} — {pendingCommission.users?.name}</div>
                  <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 2 }}>Awaiting your approval</div>
                </div>
                <button
                  onClick={approveCommission}
                  disabled={approving}
                  style={{ background: '#10B981', color: '#fff', padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 14 }}
                >
                  {approving ? 'Approving...' : '✓ Approve Commission'}
                </button>
              </div>
            </div>
          )}

          {/* AC Estimate Details */}
          {isAC && estimate && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', fontWeight: 700 }}>📋 Signed Estimate</h3>
                {(estimate as any).pdf_url ? (
                  <a
                    href={(estimate as any).pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ background: '#10B981', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}
                  >
                    📄 Download PDF
                  </a>
                ) : (
                  <button
                    onClick={() => {
                      const html = generateEstimateHTML(estimate, job);
                      const iframe = document.createElement('iframe');
                      iframe.style.display = 'none';
                      document.body.appendChild(iframe);
                      iframe.contentDocument!.write(html);
                      iframe.contentDocument!.close();
                      setTimeout(() => {
                        iframe.contentWindow!.print();
                        setTimeout(() => document.body.removeChild(iframe), 1000);
                      }, 500);
                    }}
                    style={{ background: '#4A7EB5', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', border: 'none' }}
                  >
                    🖨️ Print / Save PDF
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>TOTAL SF</span><br /><span style={{ color: '#fff', fontWeight: 600 }}>{estimate.total_sf?.toFixed(0)} SF</span></div>
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>SIGNED</span><br /><span style={{ color: '#10B981', fontWeight: 600 }}>{estimate.signed_at ? new Date(estimate.signed_at).toLocaleDateString() : '—'}</span></div>
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>PAYMENT</span><br /><span style={{ color: '#fff', fontWeight: 600, textTransform: 'capitalize' }}>{estimate.payment_method || '—'}</span></div>
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>SALESMAN</span><br /><span style={{ color: '#fff', fontWeight: 600 }}>{estimate.users?.name || '—'}</span></div>
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>COMMISSION</span><br /><span style={{ color: '#F59E0B', fontWeight: 700 }}>${estimate.salesman_commission?.toFixed(2)}</span></div>
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>STATUS</span><br /><span style={{ color: estimate.status === 'signed' ? '#10B981' : '#F59E0B', fontWeight: 700, textTransform: 'capitalize' }}>{estimate.status}</span></div>
              </div>
              {/* Notes / Specifications */}
              {(estimate as any).notes && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Notes / Specifications</div>
                  <div style={{ background: '#0F1923', borderRadius: 8, padding: '12px 14px', color: '#CBD5E1', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' as const }}>{(estimate as any).notes}</div>
                </div>
              )}
              {/* Areas */}
              {estimate.areas?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Measurements</div>
                  {estimate.areas.map((a: any, i: number) => {
                    const sf = (parseFloat(a.length) || 0) * (parseFloat(a.width) || 0);
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #2D4259' }}>
                        <span style={{ color: '#fff', fontWeight: 600 }}>{a.name}</span>
                        <span style={{ color: '#8FA8C0' }}>{a.length}' × {a.width}' = {sf.toFixed(0)} SF</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Signature */}
              {estimate.signature_image && (
                <div>
                  <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Customer Signature</div>
                  <div style={{ background: '#fff', borderRadius: 8, padding: 8, display: 'inline-block' }}>
                    <img src={estimate.signature_image} alt="Signature" style={{ height: 60, maxWidth: 280, objectFit: 'contain', display: 'block' }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Checklist Summary — roofing only (roof tab) */}
          {(isAC || isInsurance || activeTab === 'roof') && <>
          {isRoofing && checklist && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>📋 Roof Checklist</h3>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ background: '#10B98122', color: '#10B981', border: '1px solid #10B98144', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>✅ Submitted</span>
                  {checklist.pdf_url ? (
                    <a href={checklist.pdf_url} target="_blank" rel="noreferrer"
                      style={{ background: '#10B981', color: '#fff', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                      📄 Download Report
                    </a>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/leads/${(job as any).lead_id}/checklist/regenerate-pdf`);
                          alert('PDF generation started. Refresh in a few seconds.');
                        } catch (e) { alert('Failed to generate PDF'); }
                      }}
                      style={{ background: '#4A7EB5', color: '#fff', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}
                    >
                      📄 Generate PDF
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {[
                  ['Structure', checklist.structure_type],
                  ['Roof Damaged', checklist.roof_damaged],
                  ['Material', checklist.roofing_material],
                  ['Material Details', checklist.roofing_material_details],
                  ['Damaged Portions', checklist.damaged_portions?.join(', ')],
                  ['Components', checklist.roof_components?.join(', ')],
                  ['Interior Damaged', checklist.interior_damaged],
                  ['Notes', checklist.roof_notes],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label as string}>
                    <span style={{ color: '#4A6580', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
                    <div style={{ color: '#fff', fontSize: 13, marginTop: 2 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Roof Measurements & Design */}
          {isRoofing && activeTab === 'roof' && (
            <>
              {/* EagleView / Hover Report */}
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>📊 Roof Measurements</h3>
                  <div>
                    <input ref={eagleviewFileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && uploadEagleviewReport(e.target.files[0])} />
                    <button onClick={() => eagleviewFileRef.current?.click()} disabled={uploadingEagleview} style={{ background: uploadingEagleview ? '#2D4259' : '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {uploadingEagleview ? 'Parsing...' : (job as any).eagleview_report_url ? 'Upload new roof PDF' : 'Upload EagleView or HOVER PDF'}
                    </button>
                  </div>
                </div>

                {(job as any).eagleview_report_url && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#243447', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <span style={{ fontSize: 20 }}>📄</span>
                    <div style={{ flex: 1, color: '#fff', fontWeight: 600 }}>Roof Measurement Report</div>
                    <button onClick={reparseRoofReport} disabled={uploadingEagleview} style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>Recalculate from PDF</button>
                    <a href={(job as any).eagleview_report_url} target="_blank" rel="noreferrer" style={{ background: '#10B981', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>View PDF</a>
                  </div>
                )}

                {/* Parsed Measurements Grid */}
                {financials?.squares > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                    {[
                      ['Total Squares', financials.squares, 'sq', '#10B981'],
                      ['Total Area', financials.total_area_sqft, 'sq ft', '#4A7EB5'],
                      ['Pitch', financials.predominant_pitch, '', '#F59E0B'],
                      ['Facets', financials.facets, '', '#8B5CF6'],
                      ['Ridges', financials.ridges_ft, 'ft', '#fff'],
                      ['Hips', financials.hips_ft, 'ft', '#fff'],
                      ['Valleys', financials.valleys_ft, 'ft', '#fff'],
                      ['Rakes', financials.rakes_ft, 'ft', '#fff'],
                      ['Eaves', financials.eaves_ft, 'ft', '#fff'],
                      ['Drip Edge', financials.drip_edge_ft, 'ft', '#fff'],
                      ['Flashing', financials.flashing_ft, 'ft', '#fff'],
                      ['Step Flashing', financials.step_flashing_ft, 'ft', '#fff'],
                    ].filter(([, v]) => v != null).map(([label, value, unit, color]) => (
                      <div key={label as string} style={{ background: '#243447', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                        <div style={{ color: color as string, fontWeight: 700, fontSize: 18 }}>
                          {typeof value === 'number' ? value.toLocaleString() : value}
                          {unit && <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 4, color: '#8FA8C0' }}>{unit}</span>}
                        </div>
                      </div>
                    ))}
                    {financials.waste_factor_percent && (
                      <div style={{ background: '#243447', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Waste Factor</div>
                        <div style={{ color: '#F59E0B', fontWeight: 700, fontSize: 18 }}>{financials.waste_factor_percent}%</div>
                      </div>
                    )}
                    {financials.roof_complexity && (
                      <div style={{ background: '#243447', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Complexity</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{financials.roof_complexity}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ background: '#243447', borderRadius: 10, padding: '20px', textAlign: 'center', color: '#8FA8C0' }}>
                    Upload an EagleView or HOVER report to see roof measurements.
                  </div>
                )}
              </div>

              {/* Material Estimate */}
              {financials?.squares > 0 && !['ridges_ft', 'hips_ft', 'valleys_ft', 'rakes_ft', 'eaves_ft'].every(field => financials?.[field] != null) && (
                <div style={{ background: '#3B2D22', color: '#FBBF24', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                  Some roof line measurements are missing. Recalculate from the stored PDF before using the material estimate.
                </div>
              )}
              {financials?.squares > 0 && ['ridges_ft', 'hips_ft', 'valleys_ft', 'rakes_ft', 'eaves_ft'].every(field => financials?.[field] != null) && (() => {
                const sq = financials.squares || 0;
                const waste = (financials.waste_factor_percent ?? 12) / 100;
                const sqWithWaste = sq * (1 + waste);
                const ridges = financials.ridges_ft || 0;
                const hips = financials.hips_ft || 0;
                const valleys = financials.valleys_ft || 0;
                const rakes = financials.rakes_ft || 0;
                const eaves = financials.eaves_ft || 0;
                const dripEdge = financials.drip_edge_ft || (eaves + rakes);
                
                const stepFlashing = financials.step_flashing_ft || 0;
                
                // Editable estimates (from DB or defaults)
                const pipeBoots = financials.est_pipe_boots ?? 4;
                const osbSheets = financials.est_osb_sheets ?? 2;
                const sealantTubes = financials.est_sealant_tubes ?? 2;
                const nails3in = financials.est_3in_nails_boxes ?? 2;
                
                const updateEstimate = async (field: string, value: number) => {
                  try {
                    await api.patch(`/financials/${id}`, { [field]: value });
                    setFinancials((prev: any) => ({ ...prev, [field]: value }));
                  } catch (e) { console.error('Failed to update estimate', e); }
                };
                
                const calculatedMaterials = [
                  { name: 'Shingles (TAMKO Titan XT)', qty: Math.ceil(sqWithWaste * 3), unit: 'bundles', icon: '🏠', note: `${sq} sq + ${Math.round(waste*100)}% ${financials.waste_factor_percent == null ? 'planning allowance' : 'report waste'}` },
                  { name: 'Underlayment (Synthetic)', qty: Math.ceil(sqWithWaste / 10), unit: 'rolls', icon: '📜', note: '10 sq/roll' },
                  { name: 'TAMKO Moisture Guard', qty: Math.ceil((eaves / 64) + ((hips + valleys) / 64)), unit: 'rolls', icon: '🧊', note: '64 lf/roll (eaves + valleys)' },
                  { name: 'TAMKO Hip & Ridge', qty: Math.ceil((ridges + hips) / 33), unit: 'bundles', icon: '⛰️', note: '33 lf/bundle' },
                  { name: 'TAMKO Starter', qty: Math.ceil((eaves + rakes) / 102), unit: 'bundles', icon: '📏', note: '102 lf/bundle' },
                  { name: 'Ridge Vent (4\' sections)', qty: Math.ceil(ridges / 4), unit: 'pcs', icon: '🌬️', note: `${Math.round(ridges)} lf of ridge` },
                  { name: 'Drip Edge', qty: Math.ceil(dripEdge / 10), unit: 'pcs', icon: '💧', note: "10' pieces" },
                  { name: 'Coil Nails 1-1/4"', qty: Math.ceil(sqWithWaste / 15), unit: 'boxes', icon: '🔩', note: '1 box/15 sq' },
                  { name: 'Staples', qty: Math.max(1, Math.ceil(sqWithWaste / 10 / 3)), unit: 'packs', icon: '📎', note: '1 pack/3 rolls' },
                ];
                
                const editableMaterials = [
                  { name: 'Pipe Boots', qty: pipeBoots, unit: 'pcs', icon: '🚨', field: 'est_pipe_boots' },
                  { name: '3" Roof Nails', qty: nails3in, unit: 'boxes', icon: '📌', field: 'est_3in_nails_boxes' },
                  { name: 'Sealant/Caulk', qty: sealantTubes, unit: 'tubes', icon: '🪣', field: 'est_sealant_tubes' },
                  { name: 'OSB 7/16"', qty: osbSheets, unit: 'sheets', icon: '🪵', field: 'est_osb_sheets' },
                ];
                
                return (
                  <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>📦 Material Estimate</h3>
                      <span style={{ background: '#F59E0B22', color: '#F59E0B', border: '1px solid #F59E0B44', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>Auto-calculated from roof report</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                      {calculatedMaterials.map(m => (
                        <div key={m.name} style={{ background: '#243447', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 24 }}>{m.icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{m.name}</div>
                            <div style={{ color: '#4A6580', fontSize: 11 }}>{m.note}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#10B981', fontWeight: 700, fontSize: 20 }}>{m.qty}</div>
                            <div style={{ color: '#8FA8C0', fontSize: 11 }}>{m.unit}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                      <div style={{ color: '#F59E0B', fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' }}>✏️ Adjustable Items</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                        {editableMaterials.map(m => (
                          <div key={m.name} style={{ background: '#243447', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontSize: 24 }}>{m.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{m.name}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input
                                type="number"
                                value={m.qty}
                                onChange={e => updateEstimate(m.field, parseInt(e.target.value) || 0)}
                                style={{ width: 50, background: '#1A2B3C', border: '1px solid #4A6580', borderRadius: 6, padding: '4px 8px', color: '#10B981', fontWeight: 700, fontSize: 16, textAlign: 'center' }}
                                min={0}
                              />
                              <div style={{ color: '#8FA8C0', fontSize: 11 }}>{m.unit}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Project Design */}
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>🎨 Project Design</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                  {/* Shingle Line */}
                  <div>
                    <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Shingle Line</div>
                    <div style={{ background: '#243447', borderRadius: 8, padding: '10px 14px', color: '#fff', fontWeight: 600 }}>
                      {(job as any).shingle_line || 'Not selected'}
                    </div>
                  </div>
                  {/* Shingle Color */}
                  <div>
                    <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Shingle Color</div>
                    <div style={{ background: '#243447', borderRadius: 8, padding: '10px 14px', color: '#fff', fontWeight: 600 }}>
                      {(job as any).shingle_color || 'Not selected'}
                    </div>
                  </div>
                  {/* Drip Edge Color */}
                  <div>
                    <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Drip Edge Color</div>
                    <select
                      value={(job as any).drip_edge_color || ''}
                      onChange={async e => {
                        const color = e.target.value;
                        await api.patch(`/jobs/${id}`, { drip_edge_color: color });
                        setJob((prev: any) => ({ ...prev, drip_edge_color: color }));
                      }}
                      style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                    >
                      <option value="">Select color...</option>
                      <option value="White">White</option>
                      <option value="Brown">Brown</option>
                      <option value="Black">Black</option>
                    </select>
                  </div>
                </div>

                {/* Upgrades & Upsells */}
                {((job as any).roofing_upgrades?.length > 0 || (job as any).roofing_upsells?.length > 0) && (
                  <div style={{ marginTop: 20 }}>
                    {(job as any).roofing_upgrades?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Upgrades</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {(job as any).roofing_upgrades.map((u: string) => (
                            <span key={u} style={{ background: '#10B98122', color: '#10B981', border: '1px solid #10B98144', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>✓ {u}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(job as any).roofing_upsells?.length > 0 && (
                      <div>
                        <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Upsells</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {(job as any).roofing_upsells.map((u: string) => (
                            <span key={u} style={{ background: '#F59E0B22', color: '#F59E0B', border: '1px solid #F59E0B44', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>★ {u}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          </>
          }

          {/* Roofing Insurance (insurance tab) */}
          {!isAC && ['insurance','invoices'].includes(activeTab) && <PaymentArrangement key={activeTab} jobId={id!} onSaved={fetchAll} />}
          {!isAC && activeTab === 'insurance' && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>🏦 Insurance Claim</h3>
                {financials?.coverage_status && (
                  <span style={{
                    background: financials.coverage_status === 'sufficient' ? '#10B98122' : financials.coverage_status === 'underpaid' ? '#EF444422' : '#4A7EB522',
                    color: financials.coverage_status === 'sufficient' ? '#10B981' : financials.coverage_status === 'underpaid' ? '#EF4444' : '#4A7EB5',
                    border: `1px solid ${financials.coverage_status === 'sufficient' ? '#10B98144' : financials.coverage_status === 'underpaid' ? '#EF444444' : '#4A7EB544'}`,
                    borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700
                  }}>
                    {financials.coverage_status === 'sufficient' ? '✅ Sufficient Coverage' : financials.coverage_status === 'underpaid' ? `⚠️ Underpaid $${parseFloat(financials.coverage_gap || 0).toLocaleString()}` : '⏳ Pending'}
                  </span>
                )}
              </div>

              {/* Insurance Info */}
              {(job.leads?.insurance_company || job.leads?.claim_number) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  {job.leads?.insurance_company && (
                    <div style={{ background: '#243447', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Insurance Company</div>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{job.leads.insurance_company}</div>
                    </div>
                  )}
                  {job.leads?.claim_number && (
                    <div style={{ background: '#243447', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Claim Number</div>
                      <div style={{ color: '#4A7EB5', fontWeight: 700, fontSize: 14 }}>{job.leads.claim_number}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Insurance Estimate */}
              <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Insurance Estimate</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input aria-label="Insurance estimate PDF" ref={insuranceFileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && uploadInsuranceEstimate(e.target.files[0])} />
                    <button onClick={() => insuranceFileRef.current?.click()} disabled={uploadingInsurance} style={{ background: uploadingInsurance ? '#2D4259' : '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {uploadingInsurance ? '🧠 Parsing...' : financials?.insurance_estimate_url ? '🔄 Re-upload' : '+ Upload PDF'}
                    </button>
                  </div>
                </div>
                {/* Source badge */}
                {financials?.insurance_source && financials.insurance_source !== 'manual' && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, fontSize: 11, color: '#8FA8C0' }}>
                    {financials.insurance_source === 'invoice' ? (
                      <span style={{ background: '#4A7EB522', color: '#4A7EB5', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>🔗 Synced from invoice</span>
                    ) : financials.insurance_source === 'estimate_pdf' ? (
                      <span style={{ background: '#10B98122', color: '#10B981', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>📄 Parsed from PDF</span>
                    ) : null}
                    {financials.insurance_synced_at && (
                      <span style={{ color: '#4A6580' }}>{new Date(financials.insurance_synced_at).toLocaleDateString()}</span>
                    )}
                  </div>
                )}
                {/* Carrier claim figures; payment scheduling remains in Recorded Payments. */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'RCV', field: 'insurance_rcv' },
                    { label: 'ACV', field: 'insurance_acv' },
                    { label: 'Depreciation', value: financials?.insurance_parse_details?.depreciation ?? financials?.insurance_depreciation ?? 0 },
                    { label: 'Code Upgrades / Paid When Incurred', value: financials?.insurance_parse_details?.paid_when_incurred ?? 0 },
                    { label: 'Deductible', field: 'insurance_deductible' },
                    { label: 'Net Claim', field: 'insurance_net_claim' },
                  ].map(({ label, field, value }) => (
                    <div key={label as string} style={{ background: '#243447', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                      {field ? <input
                        aria-label={label}
                        type="text"
                        value={formatCurrency(financials?.[field] || 0)}
                        onChange={e => {
                          const val = parseCurrency(e.target.value);
                          setFinancials((prev: any) => ({ ...(prev || {}), [field as string]: val }));
                        }}
                        onBlur={async (e) => {
                          const val = parseCurrency(e.target.value);
                          // Manual edit = mark source as manual so future syncs behave predictably
                          try {
                            const result = await api.post(`/financials/${id}`, { [field as string]: val, insurance_source: 'manual' });
                            setFinancials(result.data);
                            await loadInvoicesAndPayments();
                          } catch (error: any) { alert(error.response?.data?.error || 'Insurance changes could not be saved.'); }
                        }}
                        placeholder="$0.00"
                        style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #2D4259', color: label === 'Net Claim' ? '#10B981' : '#fff', fontWeight: 700, fontSize: 15, width: '100%', outline: 'none', padding: '2px 0' }}
                      /> : <div aria-label={label} style={{ color: '#fff', fontWeight: 700, fontSize: 15, padding: '2px 0' }}>{formatCurrency(value)}</div>}
                    </div>
                  ))}
                  <div style={{ gridColumn: '1/-1', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* Sync from invoice — the David May flow */}
                    <button
                      disabled={syncingFromInvoice}
                      onClick={async () => {
                        setSyncingFromInvoice(true);
                        try {
                          const r = await api.post(`/financials/${id}/sync-from-invoice`, {});
                          setFinancials(r.data.financials);
                          await loadInvoicesAndPayments();
                          alert(`✓ Synced $${parseFloat(r.data.synced_from.total).toLocaleString()} from invoice ${r.data.synced_from.invoice_number}`);
                        } catch (err: any) {
                          const data = err?.response?.data;
                          if (data?.error === 'multiple_invoices') {
                            setInvoicePickerList(data.invoices || []);
                            setInvoicePickerOpen(true);
                          } else {
                            alert(data?.error || err?.message || 'Sync failed');
                          }
                        } finally {
                          setSyncingFromInvoice(false);
                        }
                      }}
                      style={{ background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', padding: '8px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: syncingFromInvoice ? 'wait' : 'pointer' }}
                    >
                      {syncingFromInvoice ? 'Syncing…' : '🔗 Sync from invoice'}
                    </button>
                    {financials?.insurance_estimate_url && (
                      <>
                        <a href={financials.insurance_estimate_url} target="_blank" rel="noreferrer" style={{ background: '#10B981', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>📄 View Estimate</a>
                        <button onClick={async () => {
                          if (!window.confirm('Remove insurance estimate? This will clear all parsed values.')) return;
                          await api.post(`/financials/${id}`, { insurance_rcv: 0, insurance_acv: 0, insurance_depreciation: 0, insurance_deductible: 0, insurance_net_claim: 0, insurance_estimate_url: null, insurance_estimate_parsed_at: null, insurance_source: 'manual', insurance_source_invoice_id: null });
                          setFinancials((prev: any) => ({ ...prev, insurance_rcv: 0, insurance_acv: 0, insurance_depreciation: 0, insurance_deductible: 0, insurance_net_claim: 0, insurance_estimate_url: null, insurance_source: 'manual', insurance_source_invoice_id: null }));
                        }} style={{ background: '#EF444422', border: '1px solid #EF444444', color: '#EF4444', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>🗑 Remove</button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Supplement */}
              <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Supplement</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input ref={supplementFileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && uploadInsuranceEstimate(e.target.files[0], true)} />
                    <button onClick={() => supplementFileRef.current?.click()} disabled={uploadingSupplement} style={{ background: uploadingSupplement ? '#2D4259' : '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {uploadingSupplement ? '🧠 Parsing...' : financials?.supplement_estimate_url ? '🔄 Re-upload' : '+ Upload PDF'}
                    </button>
                  </div>
                </div>
                {financials?.supplement_rcv > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[['Supplemented RCV (Total)', financials.supplement_rcv], ['Supplemented Net (Total)', financials.supplement_net]].map(([label, val]) => (
                      <div key={label as string} style={{ background: '#243447', borderRadius: 8, padding: '8px 12px' }}>
                        <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
                        <div style={{ color: '#10B981', fontWeight: 700, fontSize: 15 }}>${parseFloat(val as string || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    ))}
                    {/* Supplement Gain = Supplement RCV - Original RCV */}
                    {financials.supplement_rcv > 0 && financials.insurance_rcv > 0 && (() => {
                      const gain = financials.supplement_rcv - financials.insurance_rcv;
                      return (
                        <div style={{ gridColumn: '1/-1', background: gain > 0 ? '#10B98122' : '#EF444422', border: `1px solid ${gain > 0 ? '#10B98144' : '#EF444444'}`, borderRadius: 8, padding: '10px 14px' }}>
                          <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Supplement Gain</div>
                          <div style={{ color: gain > 0 ? '#10B981' : '#EF4444', fontWeight: 800, fontSize: 18 }}>
                            {gain > 0 ? '+' : ''}${gain.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div style={{ color: '#4A6580', fontSize: 11, marginTop: 2 }}>vs original estimate of ${financials.insurance_rcv.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                        </div>
                      );
                    })()}
                    {financials.supplement_estimate_url && (
                      <div style={{ gridColumn: '1/-1', marginTop: 4, display: 'flex', gap: 8 }}>
                        <a href={financials.supplement_estimate_url} target="_blank" rel="noreferrer" style={{ background: '#10B981', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>📄 View Supplement</a>
                        <button onClick={async () => {
                          if (!window.confirm('Remove supplement estimate? This will clear supplement values.')) return;
                          await api.post(`/financials/${id}`, { supplement_rcv: 0, supplement_net: 0, supplement_estimate_url: null, supplement_parsed_at: null });
                          setFinancials((prev: any) => ({ ...prev, supplement_rcv: 0, supplement_net: 0, supplement_estimate_url: null }));
                        }} style={{ background: '#EF444422', border: '1px solid #EF444444', color: '#EF4444', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>🗑 Remove</button>
                      </div>
                    )}
                  </div>
                ) : <div style={{ color: '#4A6580', fontSize: 13 }}>No supplement uploaded</div>}
              </div>

              {/* Coverage Check */}
              <div style={{ paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                <button onClick={checkCoverage} disabled={checkingCoverage} style={{ background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                  {checkingCoverage ? 'Checking...' : '🔄 Run Coverage Check'}
                </button>
                {financials?.coverage_notes && (
                  <div style={{ marginTop: 12, background: '#243447', borderRadius: 8, padding: '10px 14px', color: '#8FA8C0', fontSize: 12 }}>
                    {financials.coverage_notes}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Insurance Checks Section */}
          {!isAC && activeTab === 'insurance' && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>💵 Recorded Payments</h3>
                <button onClick={() => { setNewCheckForm(p => ({...p, check_type: ['acv','depreciation','supplement'].find(type => !paymentCategoryTaken(type)) || 'supplement'})); setShowAddCheckModal(true); }} style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  + Record Payment
                </button>
              </div>

              {/* Auto-generated Invoice banner */}
              {checksSummary?.invoice && (
                <div style={{ background: '#243447', border: '1px solid #2D4259', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18 }}>📄</span>
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                        Invoice #{checksSummary.invoice.invoice_number}
                        <span style={{
                          marginLeft: 10,
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 4,
                          textTransform: 'uppercase',
                          background: checksSummary.invoice.status === 'paid' ? '#10B98122' : checksSummary.invoice.status === 'partial' ? '#F59E0B22' : '#4A7EB522',
                          color: checksSummary.invoice.status === 'paid' ? '#10B981' : checksSummary.invoice.status === 'partial' ? '#F59E0B' : '#4A7EB5',
                        }}>{checksSummary.invoice.status}</span>
                        {checksSummary.invoice.pdf_stale && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: '#F59E0B' }}>⚠️ PDF regenerating…</span>
                        )}
                      </div>
                      <div style={{ color: '#8FA8C0', fontSize: 11, marginTop: 2 }}>
                        ${parseFloat(checksSummary.invoice.paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} paid of ${parseFloat(checksSummary.invoice.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} — auto-syncs from checks
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {checksSummary.invoice.pdf_url && (
                      <a href={checksSummary.invoice.pdf_url} target="_blank" rel="noreferrer" style={{ background: '#4A7EB522', border: '1px solid #4A7EB544', color: '#4A7EB5', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>📄 View PDF</a>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/financials/${id}/rebuild-invoice`);
                          await loadInvoicesAndPayments();
                        } catch (e: any) { alert('Rebuild failed: ' + (e?.response?.data?.error || e.message)); }
                      }}
                      style={{ background: '#24344722', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      title="Force rebuild line items and regenerate PDF"
                    >↻ Rebuild</button>
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              {checksSummary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                  <div style={{ background: '#243447', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Expected Total</div>
                    <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>${(checksSummary.expected?.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div style={{ background: '#243447', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Received</div>
                    <div style={{ color: '#10B981', fontSize: 18, fontWeight: 700 }}>${(checksSummary.received?.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div style={{ background: checksSummary.outstanding > 0 ? '#EF444422' : '#10B98122', borderRadius: 10, padding: '12px 16px', border: `1px solid ${checksSummary.outstanding > 0 ? '#EF444444' : '#10B98144'}` }}>
                    <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Outstanding</div>
                    <div style={{ color: checksSummary.outstanding > 0 ? '#EF4444' : '#10B981', fontSize: 18, fontWeight: 700 }}>${(checksSummary.outstanding || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
              )}

              {checksSummary?.payment_arrangement !== 'insurance' && checksSummary?.expected?.customer !== undefined && <p style={{color:'#dce8f3'}}>Expected insurer contribution: {formatCurrency(checksSummary.expected.insurer)} · Customer responsibility: {formatCurrency(checksSummary.expected.customer)}</p>}
              {checksSummary?.expected?.invoice_adjustment !== undefined && <p style={{color:'#8FA8C0'}}>Invoice adjustment: {formatCurrency(checksSummary.expected.invoice_adjustment)}. Claim amounts below are retained for reference.</p>}
              {/* Expected Breakdown */}
              {checksSummary?.expected && checksSummary.payment_arrangement === 'insurance' && (
                <div style={{ background: '#243447', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Expected Breakdown</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    <div>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600 }}>Deposit</div>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{formatCurrency(checksSummary.expected.acv || 0)}</div>
                      <div style={{ color: '#4A6580', fontSize: 10 }}>ACV - Deductible</div>
                    </div>
                    <div>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600 }}>Deductible</div>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{formatCurrency(checksSummary.expected.deductible || 0)}</div>
                      <div style={{ color: '#4A6580', fontSize: 10 }}>{checksSummary.deductible_paid_via_qb ? 'Paid via QB' : 'From homeowner'}</div>
                    </div>
                    <div>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600 }}>Final Payment</div>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{formatCurrency(checksSummary.expected.depreciation || 0)}</div>
                      <div style={{ color: '#4A6580', fontSize: 10 }}>After job complete</div>
                    </div>
                    <div>
                      <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600 }}>Supplement</div>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{formatCurrency(checksSummary.expected.supplement || 0)}</div>
                      <div style={{ color: '#4A6580', fontSize: 10 }}>If approved</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Check List */}
              <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Recorded Payments</div>
              {insuranceChecks.length === 0 && !(checksSummary?.payments||[]).some((p:any)=>!p.source_check_id&&p.status==='completed') ? (
                <div style={{ color: '#4A6580', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No payments recorded yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {insuranceChecks.map((check: any) => {
                    const payment = checksSummary?.payments?.find((p:any)=>p.source_check_id===check.id);
                    return <RecordedPayment key={check.id} jobId={id!} receipt={check} payment={payment}
                      onSaved={loadInvoicesAndPayments}
                      onDelete={user?.role==='admin' ? ()=>deleteInsuranceCheck(check.id) : undefined}/>;
                  })}
                  {(checksSummary?.payments||[]).filter((p:any)=>!p.source_check_id&&p.status==='completed').map((p:any)=>
                    <RecordedPayment key={p.id} jobId={id!} payment={p} onSaved={loadInvoicesAndPayments}
                      onDelete={user?.role==='admin' ? ()=>deletePayment(p) : undefined} deleting={deletingPayment!==null}/>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Invoice Picker Modal (for insurance ← invoice sync when multiple invoices exist) */}
          {invoicePickerOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, width: 480, maxWidth: '90%', border: '1px solid #2D4259' }}>
                <h3 style={{ color: '#fff', margin: '0 0 8px', fontWeight: 700 }}>🔗 Sync Insurance Estimate</h3>
                <div style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 16 }}>Multiple invoices on this job. Pick which one to use as the insurance estimate total.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                  {invoicePickerList.map(inv => (
                    <button
                      key={inv.id}
                      onClick={async () => {
                        setSyncingFromInvoice(true);
                        try {
                          const r = await api.post(`/financials/${id}/sync-from-invoice`, { invoice_id: inv.id });
                          setFinancials(r.data.financials);
                          setInvoicePickerOpen(false);
                          await loadInvoicesAndPayments();
                          alert(`✓ Synced $${parseFloat(r.data.synced_from.total).toLocaleString()} from invoice ${r.data.synced_from.invoice_number}`);
                        } catch (err: any) {
                          alert(err?.response?.data?.error || err?.message || 'Sync failed');
                        } finally {
                          setSyncingFromInvoice(false);
                        }
                      }}
                      disabled={syncingFromInvoice}
                      style={{ background: '#243447', border: '1px solid #2D4259', borderRadius: 10, padding: '12px 14px', textAlign: 'left', cursor: 'pointer', color: '#fff' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{inv.invoice_number}</div>
                          <div style={{ color: '#8FA8C0', fontSize: 11, marginTop: 2, textTransform: 'uppercase' }}>{inv.invoice_type} · {inv.status}{inv.invoice_date ? ` · ${new Date(inv.invoice_date).toLocaleDateString()}` : ''}</div>
                        </div>
                        <div style={{ color: '#10B981', fontWeight: 700, fontSize: 16 }}>${parseFloat(inv.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => setInvoicePickerOpen(false)} style={{ background: 'transparent', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Add Check Modal */}
          {showAddCheckModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, width: 420, maxWidth: '90%', border: '1px solid #2D4259' }}>
                <h3 style={{ color: '#fff', margin: '0 0 20px', fontWeight: 700 }}>💵 Record Payment</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <PayerSelect value={newCheckForm.payer_type} onChange={value=>setNewCheckForm({...newCheckForm,payer_type:value})}/>
                    <label style={{display:'block',color:'#8FA8C0',fontSize:12,fontWeight:700,marginBottom:16}}>Payment Method<select aria-label="Payment Method" value={newCheckForm.payment_method} onChange={e=>setNewCheckForm({...newCheckForm,payment_method:e.target.value})} style={{display:'block',width:'100%',marginTop:6,background:'#243447',border:'1px solid #2D4259',color:'#fff',borderRadius:8,padding:'10px 12px',fontSize:14}}>{Object.entries({cash:'Cash',check:'Check',card:'Credit/Debit Card',ach:'ACH / Bank Transfer',insurance_check:'Insurance Check',financing:'Financing',other:'Other'}).map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></label>
                    <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Payment Category *</label>
                    <select
                      value={newCheckForm.check_type}
                      onChange={e => setNewCheckForm(p => ({ ...p, check_type: e.target.value }))}
                      style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
                    >
                      <option value="acv" disabled={paymentCategoryTaken('acv')}>Deposit{paymentCategoryTaken('acv') ? ' — already recorded' : ''}</option>
                      <option value="deductible">Deductible (from homeowner)</option>
                      <option value="depreciation" disabled={paymentCategoryTaken('depreciation')}>Final Payment{paymentCategoryTaken('depreciation') ? ' — already recorded' : ''}</option>
                      <option value="supplement">Supplement Payment</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Amount *</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={newCheckForm.amount}
                        onChange={e => setNewCheckForm(p => ({ ...p, amount: e.target.value }))}
                        style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Reference #</label>
                      <input
                        type="text"
                        placeholder="e.g. 4521"
                        value={newCheckForm.check_number}
                        onChange={e => setNewCheckForm(p => ({ ...p, check_number: e.target.value }))}
                        style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Date Received</label>
                    <input
                      type="date"
                      value={newCheckForm.received_date}
                      onChange={e => setNewCheckForm(p => ({ ...p, received_date: e.target.value }))}
                      style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Notes</label>
                    <input
                      type="text"
                      placeholder="Optional notes..."
                      value={newCheckForm.notes}
                      onChange={e => setNewCheckForm(p => ({ ...p, notes: e.target.value }))}
                      style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Payment Receipt (Optional)</label>
                    <input
                      aria-label="Payment receipt"
                      ref={checkImageRef}
                      type="file"
                      accept="image/*,.pdf"
                      style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                {checkSaveError && <p role="alert" style={{ color: '#FCA5A5' }}>{checkSaveError}</p>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button
                    disabled={uploadingCheck}
                    onClick={() => { setCheckSaveError(null); setShowAddCheckModal(false); setNewCheckForm({ payment_method: 'check', payer_type: 'unknown', check_type: 'acv', check_number: '', amount: '', received_date: '', notes: '' }); }}
                    style={{ flex: 1, background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => addInsuranceCheck(checkImageRef.current?.files?.[0])}
                    disabled={uploadingCheck}
                    style={{ flex: 1, background: '#10B981', border: 'none', color: '#fff', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {uploadingCheck ? 'Saving...' : '✓ Record Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Roofing Financials (financials tab) */}
          {(isAC || activeTab === 'financials') && <>
          {!isAC && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>💰 Financials</h3>
              </div>

              {/* Squares + Dumpsters (manual until EagleView live) — roofing only */}
              {isRoofing && (
              <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Roof Squares</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="number"
                        placeholder="e.g. 24.5"
                        value={financials?.squares || ''}
                        onChange={e => setFinancials((prev: any) => ({ ...(prev || {}), squares: e.target.value }))}
                        style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14, flex: 1 }}
                      />
                      <button
                        onClick={async () => {
                          const val = parseFloat(financials?.squares);
                          if (!isNaN(val) && val > 0) {
                            await api.post(`/financials/${id}`, { squares: val });
                            setFinancials((prev: any) => ({ ...prev, squares: val }));
                            alert(`✅ Saved: ${val} squares`);
                          }
                        }}
                        style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                      >
                        Save
                      </button>
                    </div>
                    <div style={{ color: '#4A6580', fontSize: 11, marginTop: 4 }}>Auto-filled by a roof report</div>
                  </div>
                  <div>
                    <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Dumpsters</div>
                    <input
                      type="number"
                      aria-label="Dumpster quantity"
                      min="1"
                      step="1"
                      value={dumpsterQuantity}
                      onChange={e => setDumpsterCount(e.target.value)}
                      disabled={savingDumpster || dumpsterExpenses.length > 1}
                      style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box' as any }}
                    />
                    <button
                      disabled={savingDumpster || dumpsterExpenses.length > 1}
                      onClick={async () => {
                        if (dumpsterSaveLock.current) return;
                        const existing = expenses.find((e: any) => e.type === 'dumpster');
                        const quantity = Number(dumpsterQuantity);
                        if (!Number.isSafeInteger(quantity) || quantity < 1) { alert('Enter a whole dumpster quantity of at least 1.'); return; }
                        if (existing && quantity === Number(existing.quantity)) return;
                        dumpsterSaveLock.current = true;
                        setSavingDumpster(true);
                        try {
                          const payload = { type: 'dumpster', amount: quantity * 750, quantity, unit_cost: 750, description: `${quantity} Dumpster${quantity > 1 ? 's' : ''} @ $750 each` };
                          const { data } = existing
                            ? await api.patch(`/financials/${id}/expenses/${existing.id}`, payload)
                            : await api.post(`/financials/${id}/expenses`, payload);
                          setExpenses(prev => existing ? prev.map(e => e.id === existing.id ? data : e) : [...prev, data]);
                          setDumpsterCount(null);
                        } catch (err: any) {
                          alert(err.response?.data?.error || 'Could not save dumpster quantity. Refresh the financials before retrying.');
                        } finally { dumpsterSaveLock.current = false; setSavingDumpster(false); }
                      }}
                      style={{ marginTop: 6, background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}
                    >{savingDumpster ? 'Saving…' : 'Save dumpsters'}</button>
                    <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 6 }}>Up to 45 squares: 1 dumpster. Over 45 squares: 2 dumpsters. $750 each.</div>
                    <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 6 }}>Roof-size estimate: {recommendedDumpsters} dumpster{recommendedDumpsters > 1 ? 's' : ''} (${(recommendedDumpsters * 750).toLocaleString()}).</div>
                    {dumpsterExpenses.length > 1 && <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 6 }}>{recordedDumpsters} dumpsters recorded across {dumpsterExpenses.length} expense entries. Manage those entries below.</div>}
                    {dumpsterExpenses.length === 1 && recordedDumpsters !== recommendedDumpsters && <button disabled={savingDumpster} onClick={() => setDumpsterCount(String(recommendedDumpsters))}>Use roof-size estimate</button>}
                  </div>
                </div>
              </div>
              )}

              {/* Expenses */}
              <div style={{ paddingTop: 16, borderTop: '1px solid #2D4259', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Expenses</div>
                  {user?.role === 'admin' && <button onClick={() => setShowAddExpense(!showAddExpense)} style={{ background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add</button>}
                </div>
                {showAddExpense && (
                  <div style={{ background: '#243447', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                      <select value={expenseForm.type} onChange={e => setExpenseForm(p => ({ ...p, type: e.target.value }))} style={{ background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
                        {['materials', 'labor', 'eagleview', 'dumpster', 'permit', 'other'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                      <input placeholder="Description" value={expenseForm.description} onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))} style={{ background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 10px', fontSize: 13 }} />
                      <input placeholder="Amount" type="number" value={expenseForm.amount} onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))} style={{ background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 10px', fontSize: 13 }} />
                      <button onClick={addExpense} style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer' }}>Add</button>
                    </div>
                  </div>
                )}
                {expenses.length === 0 ? <div style={{ color: '#4A6580', fontSize: 13 }}>No expenses yet</div> : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {expenses.map((e: any) => (
                      <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#243447', borderRadius: 8, padding: '8px 12px' }}>
                        <div>
                          <span style={{ background: '#1A2B3C', color: '#8FA8C0', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, marginRight: 8 }}>{e.type}</span>
                          <span style={{ color: '#fff', fontSize: 13 }}>{e.description || e.type}</span>
                          {e.auto_generated && <span style={{ color: '#4A6580', fontSize: 11, marginLeft: 6 }}>auto</span>}
                          {e.receipt_parsed_at && <span style={{ color: '#F97316', fontSize: 11, marginLeft: 6 }}>🧾 AI parsed</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: '#EF4444', fontWeight: 700 }}>-${parseFloat(e.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          {e.job_documents?.file_url && (
                            <a href={e.job_documents.file_url} target="_blank" rel="noreferrer" style={{ color: '#F97316', fontSize: 11, fontWeight: 600, textDecoration: 'none' }} title="View receipt">🧾</a>
                          )}
                          {user?.role === 'admin' && <button onClick={() => deleteExpense(e.id)} style={{ background: 'none', border: 'none', color: '#4A6580', cursor: 'pointer', fontSize: 14 }}>🗑</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* P&L Summary */}
              {(financials?.invoice_total > 0 || expenses.length > 0) && (() => {
                const sq = parseFloat(financials?.squares || 0);
                // Supplement replaces original — use supplement RCV if exists
                const totalRevenue = Number(financials?.invoice_total || 0);
                const manualCosts = expenses.reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0);
                const hasLabor = expenses.some((e: any) => e.type === 'labor');
                const hasMaterials = expenses.some((e: any) => e.type === 'materials');
                const estimatedLabor = !hasLabor && sq ? sq * 110 : 0;
                // Use actual material order total if available, otherwise estimate
                const actualMaterialCost = parseFloat(financials?.material_order_total || 0);
                const estimatedMaterials = !hasMaterials 
                  ? (actualMaterialCost > 0 ? actualMaterialCost : (sq ? sq * 200 : 0))
                  : 0;
                const hasMaterialOrder = actualMaterialCost > 0;
                const commissionTotal = jobCommissions.reduce((s: number, c: any) => s + parseFloat(c.amount || 0), 0);
                const totalCosts = manualCosts + estimatedLabor + estimatedMaterials + commissionTotal;
                const netProfit = totalRevenue - totalCosts;
                return (
                  <div style={{ paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                    <div style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>P&L Summary</div>
                    {[
                      ['Invoiced revenue', totalRevenue, '#10B981'],
                      ...(sq > 0 && !hasLabor ? [[`Labor (${sq} sq × $110)`, estimatedLabor, '#EF4444']] : []),
                      ...(!hasMaterials && estimatedMaterials > 0 ? [[
                        hasMaterialOrder 
                          ? `Materials (${financials?.material_order_supplier || 'ABC/QXO'} Order)` 
                          : `Materials (${sq} sq × $200 est.)`,
                        estimatedMaterials, 
                        '#EF4444'
                      ]] : []),
                      ...(manualCosts > 0 ? [['Other Expenses', manualCosts, '#EF4444']] : []),
                      ...(commissionTotal > 0 ? [[`Commissions (${jobCommissions.length})`, commissionTotal, '#EF4444']] : []),
                      ['Total Costs', totalCosts, '#EF4444'],
                      ['Net Profit', netProfit, netProfit >= 0 ? '#10B981' : '#EF4444'],
                    ].map(([label, val, color]: any[]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #2D425944' }}>
                        <span style={{ color: '#8FA8C0', fontSize: 13 }}>{label}</span>
                        <span style={{ color, fontWeight: 700, fontSize: 14 }}>{val < 0 ? '-' : ''}${Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    <button onClick={checkCoverage} disabled={checkingCoverage} style={{ marginTop: 12, background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                      {checkingCoverage ? 'Checking...' : '🔄 Run Coverage Check'}
                    </button>
                  </div>
                );
              })()}

              {financials?.coverage_notes && (
                <div style={{ marginTop: 12, background: '#243447', borderRadius: 8, padding: '10px 14px', color: '#8FA8C0', fontSize: 12 }}>
                  {financials.coverage_notes}
                </div>
              )}
            </div>
          )}

          {/* Invoice Quick Link - directs to Invoices tab */}
          {!isAC && financials?.payment_arrangement !== 'retail' && financials?.payment_arrangement !== 'mixed' && financials?.insurance_rcv > 0 && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 16, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: '#fff', fontWeight: 700 }}>🧾 Invoices</span>
                  <span style={{ color: '#8FA8C0', fontSize: 13, marginLeft: 12 }}>Manage invoices and payments in the Invoices tab</span>
                </div>
                <button
                  onClick={() => setActiveTab('invoices')}
                  style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >
                  Go to Invoices →
                </button>
              </div>
            </div>
          )}

          {/* Roofing Payment Tracker */}
          {(isRoofing || isInsurance) && (!financials?.payment_arrangement || financials.payment_arrangement === 'insurance') && ROOFING_PAYMENT_STAGES.includes(job.pipeline_stage_name) && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <h3 style={{ color: '#fff', fontWeight: 700, margin: '0 0 16px 0' }}>💰 Payments</h3>

              {/* 3-pill status row */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                {[
                  { label: 'Deposit', paid: financials?.acv_paid, detail: financials?.acv_received_amount ? `$${parseFloat(financials.acv_received_amount).toLocaleString()}${financials.acv_check_number ? ` — Check #${financials.acv_check_number}` : ''}` : null },
                  { label: 'Deductible', paid: financials?.deductible_paid, detail: financials?.insurance_deductible ? `$${parseFloat(financials.insurance_deductible).toLocaleString()}` : null },
                  { label: 'Final Payment', paid: financials?.depreciation_released, detail: financials?.depreciation_received_amount ? `$${parseFloat(financials.depreciation_received_amount).toLocaleString()}` : null },
                ].map(({ label, paid, detail }) => (
                  <div key={label} style={{
                    background: paid ? '#10B98122' : '#F59E0B22',
                    border: `1px solid ${paid ? '#10B98144' : '#F59E0B44'}`,
                    borderRadius: 10, padding: '10px 16px', minWidth: 140,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 14 }}>{paid ? '✅' : '⏳'}</span>
                      <span style={{ color: paid ? '#10B981' : '#F59E0B', fontWeight: 800, fontSize: 14 }}>{label}</span>
                    </div>
                    {detail && <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 2 }}>{detail}</div>}
                    {!paid && !detail && <div style={{ color: '#4A6580', fontSize: 12 }}>Pending</div>}
                  </div>
                ))}
              </div>

              {/* Create invoice */}
              {!financials?.roofing_qb_invoice_id && (
                <button
                  onClick={createRoofingInvoice}
                  disabled={creatingInvoice}
                  style={{ background: creatingInvoice ? '#2D4259' : '#4A7EB5', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 12, width: '100%' }}
                >
                  {creatingInvoice ? 'Creating…' : '📄 Create QB Invoice & Send Deductible Link'}
                </button>
              )}
              {financials?.roofing_qb_invoice_id && (
                <div style={{ background: '#243447', borderRadius: 8, padding: '8px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#8FA8C0', fontSize: 13 }}>📄 QB Invoice #{financials.roofing_qb_invoice_id}</span>
                  {financials.roofing_qb_invoice_url && (
                    <a href={financials.roofing_qb_invoice_url} target="_blank" rel="noreferrer" style={{ color: '#4A7EB5', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>View in QB ↗</a>
                  )}
                </div>
              )}

              {/* ACV section */}
              {financials?.roofing_qb_invoice_id && !financials?.acv_paid && (
                <div style={{ marginBottom: 12 }}>
                  {!showAcvForm ? (
                    <button
                      onClick={() => setShowAcvForm(true)}
                      style={{ background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}
                    >
                      📬 Record Deposit
                    </button>
                  ) : (
                    <div style={{ background: '#243447', borderRadius: 10, padding: 14 }}>
                      <div style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Record Deposit</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <input
                          placeholder="Check Number (optional)"
                          value={acvCheckNumber}
                          onChange={e => setAcvCheckNumber(e.target.value)}
                          style={{ background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
                        />
                        <input
                          placeholder="Amount ($)"
                          type="number"
                          value={acvAmount}
                          onChange={e => setAcvAmount(e.target.value)}
                          style={{ background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={recordAcv} disabled={recordingPayment} style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', flex: 1 }}>
                          {recordingPayment ? 'Recording…' : '✅ Record Deposit'}
                        </button>
                        <button onClick={() => setShowAcvForm(false)} style={{ background: 'none', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Deductible status */}
              {financials?.roofing_qb_invoice_id && !financials?.deductible_paid && (
                <div style={{ background: '#243447', borderRadius: 8, padding: '8px 14px', marginBottom: 12, color: '#8FA8C0', fontSize: 13 }}>
                  📬 Deductible link sent to homeowner — awaiting payment
                  {financials.deductible_payment_link && (
                    <a href={financials.deductible_payment_link} target="_blank" rel="noreferrer" style={{ color: '#4A7EB5', fontWeight: 600, marginLeft: 8, textDecoration: 'none' }}>View Link ↗</a>
                  )}
                </div>
              )}

              {/* Depreciation section */}
              {financials?.roofing_qb_invoice_id && !financials?.depreciation_released && (
                <div style={{ marginBottom: 12 }}>
                  {!showDepreciationForm ? (
                    <button
                      onClick={() => setShowDepreciationForm(true)}
                      style={{ background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}
                    >
                      💵 Record Final Payment
                    </button>
                  ) : (
                    <div style={{ background: '#243447', borderRadius: 10, padding: 14 }}>
                      <div style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Record Final Payment</div>
                      <input
                        placeholder="Amount ($)"
                        type="number"
                        value={depreciationAmount}
                        onChange={e => setDepreciationAmount(e.target.value)}
                        style={{ background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 13, width: '100%', marginBottom: 10, boxSizing: 'border-box' as any }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={recordDepreciation} disabled={recordingPayment} style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', flex: 1 }}>
                          {recordingPayment ? 'Recording…' : '✅ Record Final Payment'}
                        </button>
                        <button onClick={() => setShowDepreciationForm(false)} style={{ background: 'none', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* All paid banner */}
              {financials?.acv_paid && financials?.deductible_paid && financials?.depreciation_released && (
                <div style={{ background: '#10B98122', border: '1px solid #10B98144', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ color: '#10B981', fontWeight: 800, fontSize: 16 }}>🎉 Fully Paid & Closed</div>
                  <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 4 }}>All three payments received — job complete</div>
                </div>
              )}
            </div>
          )}

          </>
          }

          {/* ─── WORK & MATERIALS TAB ─── */}
          {!isAC && activeTab === 'work_materials' && (
            <>
              {/* Crew Work Order */}
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>📋 Crew Work Order</h3>
                {financials?.squares ? (
                  <div style={{ background: '#243447', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                      <div><div style={{ color: '#4A6580', fontSize: 11 }}>SQUARES</div><div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{financials.squares}</div></div>
                      <div><div style={{ color: '#4A6580', fontSize: 11 }}>RATE / SQ</div><div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>$110</div></div>
                      <div><div style={{ color: '#4A6580', fontSize: 11 }}>CREW TOTAL</div><div style={{ color: '#10B981', fontWeight: 800, fontSize: 18 }}>${(parseFloat(financials.squares) * 110).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div></div>
                      {(job as any).shingle_color && <div><div style={{ color: '#4A6580', fontSize: 11 }}>COLOR</div><div style={{ color: '#fff', fontWeight: 700 }}>{(job as any).shingle_color}</div></div>}
                    </div>
                  </div>
                ) : isRoofing ? (
                  <div style={{ background: '#243447', borderRadius: 10, padding: '12px 16px', color: '#F59E0B', fontSize: 13, marginBottom: 12 }}>
                    ⚠️ Upload an EagleView or HOVER report in the Roof tab first to get roof squares.
                  </div>
                ) : null}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={generateWorkOrder}
                    disabled={generatingWorkOrder}
                    style={{ background: generatingWorkOrder ? '#2D4259' : '#10B981', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: generatingWorkOrder ? 'not-allowed' : 'pointer' }}
                  >
                    {generatingWorkOrder ? 'Generating...' : 'Generate Work Order PDF'}
                  </button>
                  {(workOrderUrl || (job as any).work_order_pdf_url) && (
                    <a href={workOrderUrl || (job as any).work_order_pdf_url} target="_blank" rel="noreferrer"
                      style={{ background: '#243447', color: '#4A7EB5', border: '1px solid #4A7EB544', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}
                    >View Work Order</a>
                  )}
                </div>
              </div>

              {/* Materials — ABC/QXO panel is roofing-specific (shingle suppliers) */}
              {isRoofing && (
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>📦 Material Order</h3>

                {/* ABC/QXO Material Order */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>ABC/QXO Material Order</div>
                    <div>
                      <input ref={qxoFileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && uploadQxoOrder(e.target.files[0])} />
                      <button onClick={() => qxoFileRef.current?.click()} disabled={uploadingQxo} style={{ background: uploadingQxo ? '#2D4259' : '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        {uploadingQxo ? '🧠 Parsing...' : (job as any).qxo_order_url ? '🔄 Replace' : '+ Upload PDF'}
                      </button>
                    </div>
                  </div>
                  {(job as any).qxo_order_url ? (
                    <div style={{ background: '#243447', borderRadius: 10, padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: financials?.material_order_total ? 12 : 0 }}>
                        <span style={{ fontSize: 20 }}>📦</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#fff', fontWeight: 600 }}>
                            {financials?.material_order_supplier || 'Material Order'}
                            {financials?.material_order_number && <span style={{ color: '#4A6580', fontWeight: 400, marginLeft: 8 }}>#{financials.material_order_number}</span>}
                          </div>
                          {(job as any).qxo_ordered_at && <div style={{ color: '#4A6580', fontSize: 12 }}>Ordered {new Date((job as any).qxo_ordered_at).toLocaleDateString()}</div>}
                        </div>
                        <a href={(job as any).qxo_order_url} target="_blank" rel="noreferrer" style={{ background: '#10B981', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>View Order</a>
                      </div>
                      {/* Material Order Cost - editable */}
                      {financials?.material_order_total !== undefined && financials?.material_order_total !== null ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                          <div style={{ background: '#1A2B3C', borderRadius: 8, padding: '8px 12px' }}>
                            <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Subtotal</div>
                            <input
                              type="text"
                              value={financials.material_order_subtotal ? `$${parseFloat(financials.material_order_subtotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}
                              onChange={e => {
                                const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                setFinancials((prev: any) => ({ ...prev, material_order_subtotal: val }));
                              }}
                              onBlur={async e => {
                                const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                await api.post(`/financials/${id}/material-order`, { subtotal: val });
                              }}
                              style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #2D4259', color: '#fff', fontWeight: 700, fontSize: 14, width: '100%', outline: 'none', padding: '2px 0' }}
                            />
                          </div>
                          <div style={{ background: '#1A2B3C', borderRadius: 8, padding: '8px 12px' }}>
                            <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Tax</div>
                            <input
                              type="text"
                              value={financials.material_order_tax ? `$${parseFloat(financials.material_order_tax).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}
                              onChange={e => {
                                const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                setFinancials((prev: any) => ({ ...prev, material_order_tax: val }));
                              }}
                              onBlur={async e => {
                                const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                await api.post(`/financials/${id}/material-order`, { tax: val });
                              }}
                              style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #2D4259', color: '#fff', fontWeight: 700, fontSize: 14, width: '100%', outline: 'none', padding: '2px 0' }}
                            />
                          </div>
                          <div style={{ background: '#10B98122', borderRadius: 8, padding: '8px 12px', border: '1px solid #10B98144' }}>
                            <div style={{ color: '#10B981', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Total</div>
                            <input
                              type="text"
                              value={financials.material_order_total ? `$${parseFloat(financials.material_order_total).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}
                              onChange={e => {
                                const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                setFinancials((prev: any) => ({ ...prev, material_order_total: val }));
                              }}
                              onBlur={async e => {
                                const val = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                                await api.post(`/financials/${id}/material-order`, { total: val });
                              }}
                              style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #10B981', color: '#10B981', fontWeight: 800, fontSize: 16, width: '100%', outline: 'none', padding: '2px 0' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, padding: '10px 12px', background: '#1A2B3C', borderRadius: 8, border: '1px dashed #2D4259' }}>
                          <div style={{ color: '#4A6580', fontSize: 11, marginBottom: 6 }}>Enter material order total:</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              type="text"
                              placeholder="$0.00"
                              style={{ flex: 1, background: '#243447', border: '1px solid #2D4259', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 14, outline: 'none' }}
                              onKeyDown={async e => {
                                if (e.key === 'Enter') {
                                  const val = parseFloat((e.target as HTMLInputElement).value.replace(/[^0-9.]/g, '')) || 0;
                                  if (val > 0) {
                                    const { data } = await api.post(`/financials/${id}/material-order`, { total: val });
                                    setFinancials(data.financials);
                                  }
                                }
                              }}
                            />
                            <button
                              onClick={async () => {
                                const input = document.querySelector('input[placeholder="$0.00"]') as HTMLInputElement;
                                const val = parseFloat(input?.value.replace(/[^0-9.]/g, '')) || 0;
                                if (val > 0) {
                                  const { data } = await api.post(`/financials/${id}/material-order`, { total: val });
                                  setFinancials(data.financials);
                                }
                              }}
                              style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                            >Save</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ background: '#243447', borderRadius: 10, padding: '12px 16px', color: '#8FA8C0', fontSize: 13 }}>
                      No material order uploaded yet.
                    </div>
                  )}
                </div>
              </div>
              )}
            </>
          )}

          {/* ─── INVOICES TAB ─── */}
          {!isAC && activeTab === 'invoices' && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>🧾 Invoices & Payments</h3>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setShowRecordPaymentModal(true)}
                    style={{ background: '#10B981', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}
                  >
                    + Record Payment
                  </button>
                  <button
                    onClick={() => setShowNewInvoiceModal(true)}
                    style={{ background: '#4A7EB5', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}
                  >
                    + New Invoice
                  </button>
                </div>
              </div>

              {/* Invoices Table */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ color: '#4A7EB5', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>Invoices</div>
                {loadingInvoices ? (
                  <div style={{ color: '#8FA8C0', padding: 20, textAlign: 'center' }}>Loading...</div>
                ) : invoices.length === 0 ? (
                  <div style={{ background: '#243447', borderRadius: 10, padding: 20, textAlign: 'center', color: '#8FA8C0' }}>
                    No invoices yet. <button onClick={() => setShowNewInvoiceModal(true)} style={{ background: 'none', border: 'none', color: '#4A7EB5', cursor: 'pointer', fontWeight: 700 }}>Create one</button>
                  </div>
                ) : (
                  <div style={{ background: '#243447', borderRadius: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#1E3A5F', borderRadius: '10px 10px 0 0' }}>
                          <th style={{ padding: '10px 12px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderRadius: '10px 0 0 0' }}>Invoice #</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Date</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Type</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Total</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Paid</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Due</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Status</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv: any) => {
                          const statusColors: any = { draft: '#6B7280', open: '#F59E0B', partial: '#3B82F6', paid: '#10B981', void: '#EF4444' };
                          return (
                            <tr key={inv.id} style={{ borderBottom: '1px solid #1E3A5F' }}>
                              <td style={{ padding: '10px 12px', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 13 }}>{inv.invoice_number}</td>
                              <td style={{ padding: '10px 12px', color: '#8FA8C0', fontSize: 12 }}>{new Date(inv.invoice_date).toLocaleDateString()}</td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={{ background: '#4A7EB522', color: '#4A7EB5', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'capitalize' }}>
                                  {inv.invoice_type.replace('_', ' ')}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', color: '#fff', textAlign: 'right', fontWeight: 600, fontSize: 13 }}>${inv.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '10px 12px', color: '#10B981', textAlign: 'right', fontSize: 13 }}>${inv.paid_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '10px 12px', color: inv.due_amount > 0 ? '#F59E0B' : '#8FA8C0', textAlign: 'right', fontWeight: 600, fontSize: 13 }}>${inv.due_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <select
                                  value={inv.status}
                                  onChange={async (e) => {
                                    try {
                                      await api.patch(`/roofing-invoices/${inv.id}`, { status: e.target.value });
                                      loadInvoicesAndPayments();
                                    } catch (err) { alert('Failed to update status'); }
                                  }}
                                  style={{ background: '#243447', color: statusColors[inv.status] || '#8FA8C0', border: '1px solid #2D4259', borderRadius: 4, padding: '4px 6px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                                >
                                  <option value="draft">Draft</option>
                                  <option value="open">Open</option>
                                  <option value="partial">Partial</option>
                                  <option value="paid">Paid</option>
                                  <option value="void">Void</option>
                                </select>
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center', position: 'relative' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenInvoiceMenu(openInvoiceMenu === inv.id ? null : inv.id); }}
                                  style={{ background: '#2D4259', border: 'none', color: '#8FA8C0', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 14 }}
                                >⋮</button>
                                {openInvoiceMenu === inv.id && (
                                  <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, bottom: '100%', marginBottom: 4, background: '#1E3A5F', border: '1px solid #2D4259', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 9999, minWidth: 200, padding: '4px 0' }}>
                                    <button
                                      onClick={() => {
                                        setOpenInvoiceMenu(null);
                                        setEditingInvoice(inv);
                                        setNewInvoiceForm({
                                          invoice_type: inv.invoice_type,
                                          description: inv.description || '',
                                          line_items: inv.line_items?.length ? inv.line_items : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }]
                                        });
                                        setInsuranceAmounts(insuranceInvoiceFields(inv.insurance_amounts));
                                        setInvoiceTotal(String(inv.total));
                                        setShowNewInvoiceModal(true);
                                      }}
                                      style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderBottom: '1px solid #2D4259' }}
                                    >✏️ Edit Invoice</button>
                                    {inv.pdf_url && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); window.open(inv.pdf_url, '_blank'); setOpenInvoiceMenu(null); }}
                                        style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#4A7EB5', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderBottom: '1px solid #2D4259' }}
                                      >📄 View PDF</button>
                                    )}
                                    <button
                                      onClick={async () => {
                                        setOpenInvoiceMenu(null);
                                        try {
                                          await api.post(`/roofing-invoices/${inv.id}/generate-pdf`);
                                          loadInvoicesAndPayments();
                                        } catch (e: any) { alert('PDF generation failed: ' + (e.response?.data?.error || e.message)); }
                                      }}
                                      style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#F59E0B', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderBottom: '1px solid #2D4259' }}
                                    >🔄 {inv.pdf_url ? 'Regenerate' : 'Generate'} PDF</button>
                                    <button
                                      onClick={() => { setOpenInvoiceMenu(null); setNewPaymentForm({ ...newPaymentForm, invoice_id: inv.id, amount: String(inv.due_amount || '') }); setShowRecordPaymentModal(true); }}
                                      style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#10B981', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderBottom: '1px solid #2D4259' }}
                                    >💵 Record Payment</button>
                                    <button
                                      onClick={() => { setOpenInvoiceMenu(null); alert('Send invoice coming soon'); }}
                                      style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#8FA8C0', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderBottom: '1px solid #2D4259' }}
                                    >✉️ Send Invoice</button>
                                    <button
                                      onClick={async () => {
                                        setOpenInvoiceMenu(null);
                                        if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
                                        try {
                                          await api.delete(`/roofing-invoices/${inv.id}`);
                                          loadInvoicesAndPayments();
                                        } catch (e: any) { alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
                                      }}
                                      style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 13, textAlign: 'left' }}
                                    >🗑️ Delete Invoice</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Payments Table */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ color: '#10B981', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>Payments</div>
                {payments.length === 0 ? (
                  <div style={{ background: '#243447', borderRadius: 10, padding: 16, textAlign: 'center', color: '#8FA8C0', fontSize: 13 }}>
                    No payments recorded yet.
                  </div>
                ) : (
                  <div style={{ background: '#243447', borderRadius: 10, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#1E3A5F' }}>
                          <th style={{ padding: '10px 14px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Payment #</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Date</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Method</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Reference</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Applied To</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Amount</th>
                          <th style={{ padding: '10px 14px', color: '#8FA8C0', fontSize: 11 }}>RECEIPT / ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((pmt: any) => {
                          const check = insuranceChecks.find(c => c.id === pmt.source_check_id);
                          return (
                          <tr key={pmt.id} style={{ borderBottom: '1px solid #1E3A5F' }}>
                            <td style={{ padding: '12px 14px', color: '#fff', fontWeight: 600 }}>{pmt.payment_number}</td>
                            <td style={{ padding: '12px 14px', color: '#8FA8C0', fontSize: 13 }}>{new Date(pmt.payment_date).toLocaleDateString()}</td>
                            <td style={{ padding: '12px 14px', color: '#8FA8C0', fontSize: 13, textTransform: 'capitalize' }}>{pmt.payment_method?.replace('_', ' ')}</td>
                            <td style={{ padding: '12px 14px', color: '#8FA8C0', fontSize: 13 }}>{pmt.reference_number || '-'}</td>
                            <td style={{ padding: '12px 14px', color: '#4A7EB5', fontSize: 13 }}>{pmt.invoice?.invoice_number || 'General'}</td>
                            <td style={{ padding: '12px 14px', color: '#10B981', textAlign: 'right', fontWeight: 600 }}>${pmt.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '12px 14px' }}>
                              <PaymentActions jobId={id!} receipt={check} payment={pmt} onSaved={loadInvoicesAndPayments}
                                onDelete={user?.role==='admin' ? ()=>deletePayment(pmt) : undefined} deleting={deletingPayment!==null}/>

                            </td>
                          </tr>
                        ); })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div style={{ background: '#243447', borderRadius: 10, padding: 16, marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#8FA8C0', fontSize: 12 }}>Total Invoiced</div>
                    <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>${invoices.reduce((sum, inv) => sum + (inv.status !== 'void' ? (inv.total || 0) : 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div style={{ color: '#8FA8C0', fontSize: 12 }}>Total Paid</div>
                    <div style={{ color: '#10B981', fontSize: 20, fontWeight: 700 }}>${payments.reduce((sum, pmt) => sum + (pmt.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div style={{ color: '#8FA8C0', fontSize: 12 }}>Outstanding</div>
                    <div style={{ color: '#F59E0B', fontSize: 20, fontWeight: 700 }}>${invoices.reduce((sum, inv) => sum + (inv.status !== 'void' ? (inv.due_amount || 0) : 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* New Invoice Modal */}
          {showNewInvoiceModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, width: 500, maxHeight: '80vh', overflow: 'auto' }}>
                <h3 style={{ color: '#fff', marginBottom: 20 }}>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</h3>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Invoice Type</label>
                  <select
                    value={newInvoiceForm.invoice_type}
                    aria-label="Invoice Type"
                    disabled={!!editingInvoice && ['insurance','retail','mixed'].includes(editingInvoice.invoice_type)}
                    onChange={e => {
                      setNewInvoiceForm({ ...newInvoiceForm, invoice_type: e.target.value });
                      if (e.target.value === 'insurance') setInsuranceAmounts(insuranceInvoiceFields(null));
                    }}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 14 }}
                  >
                    <option value="insurance">Insurance</option>
                    <option value="supplement">Supplement</option>
                    <option value="upgrade">Upgrade</option>
                    <option value="change_order">Change Order</option>
                    <option value="custom">Custom</option>{editingInvoice?.invoice_type === 'retail' && <option value="retail">Retail</option>}{editingInvoice?.invoice_type === 'mixed' && <option value="mixed">Insurance + customer</option>}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Description</label>
                  <input
                    type="text"
                    value={newInvoiceForm.description}
                    onChange={e => setNewInvoiceForm({ ...newInvoiceForm, description: e.target.value })}
                    placeholder="Invoice description"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 14 }}
                  />
                </div>

                {newInvoiceForm.invoice_type === 'insurance' ? (
                  <div style={{ marginBottom: 16 }}>
                    {editingInvoice && <div style={{marginBottom:20}}>
                      <label style={{color:'#fff'}}>Invoice Total
                        <CurrencyInput aria-label="Invoice Total" value={invoiceTotal} placeholder="Automatic from claim and add-ons" onValueChange={setInvoiceTotal} style={{display:'block',width:'100%',boxSizing:'border-box',padding:10,marginTop:8,borderRadius:8,background:'#243447',color:'#fff',border:'1px solid #4A6580'}}/>
                      </label>
                      <div style={{display:'flex',gap:12,marginTop:10}}><button type="button" style={{background:'#243447',color:'#DCE8F3',border:'1px solid #4A6580',borderRadius:8,padding:8,cursor:'pointer'}} onClick={()=>setInvoiceTotal(Number(editingInvoice.paid_amount||0).toFixed(2))} disabled={!(Number(editingInvoice.paid_amount)>0)}>Use Payments Total</button><button type="button" style={{background:'#243447',color:'#DCE8F3',border:'1px solid #4A6580',borderRadius:8,padding:8,cursor:'pointer'}} onClick={()=>setInvoiceTotal('')}>Use Claim Total</button></div>
                      <p style={{color:'#8FA8C0',fontSize:12}}>Set the agreed bill independently of the insurance claim. Payments stay unchanged. The adjustment is retained when the invoice is rebuilt.</p>
                    </div>}
                    <div style={{ color: '#8FA8C0', fontSize: 12, marginBottom: 8 }}>Insurance Line Items</div>
                    {([['rcv', 'RCV (Total)'], ['acv', 'ACV (Deposit)'], ['depreciation', 'Depreciation (Final Check)'], ['deductible', 'Deductible']] as const).map(([key, label]) => (
                      <label key={key} style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', marginBottom: 10 }}>
                        {label}
                        <CurrencyInput aria-label={label}
                          value={key === 'depreciation' ? invoiceDepreciation.toFixed(2) : insuranceAmounts[key]} readOnly={key === 'depreciation'}
                          onValueChange={value => { if (key !== 'depreciation') setInsuranceAmounts({ ...insuranceAmounts, [key]: value }); }}
                          style={{ width: 160, padding: 8, borderRadius: 6, background: '#243447', color: '#fff', border: '1px solid #2D4259' }} />
                      </label>
                    ))}
                    <p style={{ color: '#8FA8C0', fontSize: 12 }}>RCV is the total. Enter ACV before the deductible; the carrier deposit is ACV minus the deductible. The homeowner pays the deductible, already included in RCV. Depreciation is calculated from the claim. Saving does not record a payment.</p>
                    <p style={{ color: '#8FA8C0', fontSize: 12 }}>Deposit: {formatCurrency(Math.max(0, Number(insuranceAmounts.acv) - Number(insuranceAmounts.deductible)))} · Remaining claim amount: {formatCurrency(Math.min(Math.max(0, Number(insuranceAmounts.rcv) - Number(insuranceAmounts.acv)), Math.max(0, Number(insuranceAmounts.rcv) - Number(insuranceAmounts.deductible))))}</p>
                    {invoiceDetailsMatch && (Number(invoiceClaimDetails.paid_when_incurred) > 0 || Number(invoiceClaimDetails.non_recoverable_depreciation) > 0) && <p style={{color:'#8FA8C0',fontSize:12}}>Final check depends on recoverable depreciation and code upgrades / paid-when-incurred items. Non-recoverable amounts remain homeowner responsibility; see the claim and expected payment breakdown.</p>}
                  </div>
                ) : (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Line Items</label>
                  {newInvoiceForm.line_items.map((item: any, idx: number) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={e => {
                          const items = [...newInvoiceForm.line_items];
                          items[idx].description = e.target.value;
                          setNewInvoiceForm({ ...newInvoiceForm, line_items: items });
                        }}
                        style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 13 }}
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={e => {
                          const items = [...newInvoiceForm.line_items];
                          items[idx].quantity = parseFloat(e.target.value) || 1;
                          items[idx].amount = items[idx].quantity * items[idx].unit_price;
                          setNewInvoiceForm({ ...newInvoiceForm, line_items: items });
                        }}
                        style={{ width: 60, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 13 }}
                      />
                      <CurrencyInput
                        aria-label={`Line ${idx + 1} unit price`}
                        placeholder="$0.00"
                        value={item.unit_price}
                        onValueChange={value => {
                          const items = [...newInvoiceForm.line_items];
                          items[idx].unit_price = parseFloat(value) || 0;
                          items[idx].amount = items[idx].quantity * items[idx].unit_price;
                          setNewInvoiceForm({ ...newInvoiceForm, line_items: items });
                        }}
                        style={{ width: 110, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 13 }}
                      />
                      <div style={{ width: 110, boxSizing: 'border-box', whiteSpace: 'nowrap', padding: '8px 10px', color: '#10B981', fontWeight: 600 }}>{formatCurrency(item.amount || 0)}</div>
                      <button
                        onClick={() => {
                          const items = newInvoiceForm.line_items.filter((_: any, i: number) => i !== idx);
                          setNewInvoiceForm({ ...newInvoiceForm, line_items: items.length ? items : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] });
                        }}
                        style={{ background: '#EF444422', color: '#EF4444', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    onClick={() => setNewInvoiceForm({ ...newInvoiceForm, line_items: [...newInvoiceForm.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0 }] })}
                    style={{ background: '#4A7EB522', color: '#4A7EB5', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >+ Add Line</button>
                </div>

                )}

                <div style={{ background: '#243447', borderRadius: 8, padding: 12, marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', fontWeight: 700 }}>
                    <span>{newInvoiceForm.invoice_type === 'insurance' && !editingInvoice ? 'Claim RCV:' : 'Invoice Total:'}</span>
                    <span>{formatCurrency(newInvoiceForm.invoice_type === 'insurance' ? (editingInvoice && invoiceTotal !== '' ? Number(invoiceTotal) : Number(insuranceAmounts.rcv)) : newInvoiceForm.line_items.reduce((sum: number, item: any) => sum + (item.amount || 0), 0))}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowNewInvoiceModal(false); setEditingInvoice(null); setNewInvoiceForm({ invoice_type: 'custom', description: '', line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] }); }}
                    style={{ background: '#243447', color: '#8FA8C0', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}
                  >Cancel</button>
                  <button
                    onClick={async () => {
                      setSavingInvoice(true);
                      try {
                        if (editingInvoice) {
                          await api.patch(`/roofing-invoices/${editingInvoice.id}`, {
                            invoice_type: newInvoiceForm.invoice_type,
                            description: newInvoiceForm.description,
                            ...(newInvoiceForm.invoice_type === 'insurance' ? {
                              ...(['acv','rcv','deductible'].some(key => Number((insuranceAmounts as any)[key]) !== Number(editingInvoice.insurance_amounts?.[key])) ? {insurance_amounts:insuranceAmounts} : {}),
                              ...(invoiceTotal === '' || Number(invoiceTotal) !== Number(editingInvoice.total) || editingInvoice.invoice_total_override != null ? {invoice_total:invoiceTotal === '' ? null : invoiceTotal} : {}),
                            } : { line_items: newInvoiceForm.line_items }),
                          });
                        } else {
                          await api.post('/roofing-invoices', {
                            job_id: id,
                            invoice_type: newInvoiceForm.invoice_type,
                            description: newInvoiceForm.description,
                            ...(newInvoiceForm.invoice_type === 'insurance' ? { insurance_amounts: insuranceAmounts } : { line_items: newInvoiceForm.line_items }),
                          });
                        }
                        setShowNewInvoiceModal(false);
                        setEditingInvoice(null);
                        setNewInvoiceForm({ invoice_type: 'custom', description: '', line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] });
                        loadInvoicesAndPayments();
                      } catch (e: any) { alert(e.response?.data?.error || e.message); }
                      finally { setSavingInvoice(false); }
                    }}
                    disabled={savingInvoice || (newInvoiceForm.invoice_type === 'insurance' ? !(Number(insuranceAmounts.rcv) > 0) : newInvoiceForm.line_items.every((i: any) => !i.description))}
                    style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, opacity: savingInvoice ? 0.6 : 1 }}
                  >{savingInvoice ? 'Saving...' : (editingInvoice ? 'Save Changes' : 'Create Invoice')}</button>
                </div>
              </div>
            </div>
          )}

          {/* Record Payment Modal */}
          {showRecordPaymentModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, width: 400, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ color: '#fff', marginBottom: 20 }}>Record Payment</h3>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Apply to Invoice</label>
                  <select
                    value={newPaymentForm.invoice_id}
                    onChange={e => setNewPaymentForm({ ...newPaymentForm, invoice_id: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 14 }}
                  >
                    <option value="">General (not applied to invoice)</option>
                    {invoices.filter((inv: any) => inv.status !== 'void' && inv.status !== 'paid').map((inv: any) => (
                      <option key={inv.id} value={inv.id}>{inv.invoice_number} - Due: {formatCurrency(inv.due_amount)}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Amount</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label="Payment amount"
                    value={paymentAmountFocused ? newPaymentForm.amount : formatCurrency(newPaymentForm.amount || 0)}
                    onFocus={() => setPaymentAmountFocused(true)}
                    onBlur={() => setPaymentAmountFocused(false)}
                    onChange={e => setNewPaymentForm({ ...newPaymentForm, amount: e.target.value.replace(/[$,]/g, '') })}
                    placeholder="$0.00"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 14 }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <PayerSelect value={newPaymentForm.payer_type} onChange={value=>setNewPaymentForm({...newPaymentForm,payer_type:value})}/>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Payment Method</label>
                  <select
                    value={newPaymentForm.payment_method}
                    onChange={e => setNewPaymentForm({ ...newPaymentForm, payment_method: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 14 }}
                  >
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="ach">ACH / Bank Transfer</option>
                    <option value="insurance_check">Insurance Check</option>
                    <option value="financing">Financing</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Reference # (Check #, etc.)</label>
                  <input
                    type="text"
                    value={newPaymentForm.reference_number}
                    onChange={e => setNewPaymentForm({ ...newPaymentForm, reference_number: e.target.value })}
                    placeholder="Optional"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2D4259', background: '#243447', color: '#fff', fontSize: 14 }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: '#8FA8C0', fontSize: 12, marginBottom: 6 }}>Check Photo / Payment Receipt (Optional)</div>
                  <label style={{ display: 'inline-block', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 12px', color: '#8FA8C0', cursor: 'pointer' }}>
                    📎 {paymentReceiptFile ? 'Change Check Photo / Receipt' : 'Upload Check Photo / Receipt'}
                    <input aria-label="Check photo or payment receipt" type="file" accept="image/*,.pdf" disabled={savingPayment} style={{ display: 'none' }} onChange={e => setPaymentReceiptFile(e.target.files?.[0] || null)} />
                  </label>
                  {paymentReceiptFile && <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 6, overflowWrap: 'anywhere' }}>{paymentReceiptFile.name}</div>}
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button
                    disabled={savingPayment}
                    onClick={() => { setPaymentReceiptFile(null); setPaymentAmountFocused(false); setShowRecordPaymentModal(false); setNewPaymentForm({ payer_type: 'unknown', invoice_id: '', amount: '', payment_method: 'check', reference_number: '', internal_note: '' }); }}
                    style={{ background: '#243447', color: '#8FA8C0', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}
                  >Cancel</button>
                  <button
                    onClick={async () => {
                      if (newPaymentForm.payer_type === 'unknown') { alert('Choose the payer.'); return; }
                      const amount = Number(newPaymentForm.amount);
                      if (!Number.isFinite(amount) || amount <= 0) { alert('Enter a positive payment amount.'); return; }
                      setSavingPayment(true);
                      try {
                        const { data: payment } = await api.post('/roofing-invoices/payments', {
                          job_id: id,
                          invoice_id: newPaymentForm.invoice_id || null,
                          amount,
                          payment_method: newPaymentForm.payment_method,
                          payer_type: newPaymentForm.payer_type,
                          reference_number: newPaymentForm.reference_number,
                        });
                        let receiptWarning = '';
                        if (paymentReceiptFile) {
                          try {
                            const body = new FormData();
                            body.append('receipt', paymentReceiptFile);
                            await api.post(`/roofing-invoices/payments/${payment.id}/attachment`, body);
                          } catch {
                            receiptWarning = 'Payment saved, but the receipt upload failed. Use Add Receipt on the payment row to retry; do not record another payment.';
                          }
                        }
                        setPaymentReceiptFile(null);
                        setPaymentAmountFocused(false);
                        setShowRecordPaymentModal(false);
                        setNewPaymentForm({ payer_type: 'unknown', invoice_id: '', amount: '', payment_method: 'check', reference_number: '', internal_note: '' });
                        await loadInvoicesAndPayments();
                        if (receiptWarning || payment.warning) alert([receiptWarning, payment.warning].filter(Boolean).join('\n\n'));
                      } catch (e: any) { alert(e.response?.data?.error || e.message); }
                      finally { setSavingPayment(false); }
                    }}
                    disabled={savingPayment || !newPaymentForm.amount}
                    style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, opacity: savingPayment ? 0.6 : 1 }}
                  >{savingPayment ? 'Saving...' : 'Record Payment'}</button>
                </div>
              </div>
            </div>
          )}

          {/* ─── CONTRACT TAB ─── */}
          {!isAC && activeTab === 'contract' && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, border: '1px solid #2D4259', marginBottom: 20 }}>
              <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>✍️ Contract</h3>

              {/* Signed Contract */}
              <div>
                <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Signed Contract</div>
                {(job as any).contract_signed ? (
                  <div style={{ background: '#243447', borderRadius: 10, padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <span style={{ fontSize: 28, background: '#10B98122', padding: '8px 12px', borderRadius: 10 }}>✅</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#10B981', fontWeight: 700, fontSize: 16 }}>Contract Signed</div>
                        <div style={{ color: '#4A6580', fontSize: 12 }}>{(job as any).contract_signed_at ? new Date((job as any).contract_signed_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}</div>
                      </div>
                      {(job as any).contract_pdf_url && (
                        <a href={(job as any).contract_pdf_url} target="_blank" rel="noreferrer" style={{ background: '#4A7EB5', color: '#fff', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>📄 View Contract PDF</a>
                      )}
                    </div>
                    
                    {/* Project Design Details */}
                    {((job as any).shingle_color || (job as any).warranty_tier || (job as any).selected_upgrades?.length > 0) && (
                      <div style={{ borderTop: '1px solid #2D4259', paddingTop: 16, marginTop: 8 }}>
                        <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>Project Design Selections</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                          {(job as any).shingle_color && (
                            <div style={{ background: '#1A2B3C', borderRadius: 8, padding: '12px 14px', border: '1px solid #2D4259' }}>
                              <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>🎨 Shingle Color</div>
                              <div style={{ color: '#fff', fontWeight: 600 }}>{(job as any).shingle_color}</div>
                            </div>
                          )}
                          {(job as any).warranty_tier && (
                            <div style={{ background: '#1A2B3C', borderRadius: 8, padding: '12px 14px', border: '1px solid #2D4259' }}>
                              <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>🛡️ Warranty</div>
                              <div style={{ color: '#fff', fontWeight: 600 }}>
                                {(job as any).warranty_tier === 'base' ? '10-Year Base' :
                                 (job as any).warranty_tier === 'gold' ? '20-Year Gold' :
                                 (job as any).warranty_tier === 'platinum' ? '50-Year Platinum' :
                                 (job as any).warranty_tier === 'master_shield' ? '50-Year Master Shield' :
                                 (job as any).warranty_tier}
                              </div>
                            </div>
                          )}
                        </div>
                        {(job as any).selected_upgrades?.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ color: '#4A6580', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>⬆️ Upgrades Selected</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {(job as any).selected_upgrades.map((u: string) => (
                                <span key={u} style={{ background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                                  {u.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ background: '#243447', borderRadius: 10, padding: '16px', color: '#8FA8C0', fontSize: 13 }}>
                    Contract not yet signed.
                    <button onClick={async () => {
                      const email = prompt('Send signing link to email:');
                      if (!email) return;
                      try { await api.post(`/jobs/${id}/roofing/contract/send`, { send_to: email }); alert('Signing link sent!'); }
                      catch { alert('Failed to send'); }
                    }} style={{ marginLeft: 12, background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Send to Sign</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── FIELD DOCUMENTATION (shown when available) ─── */}
          {/* Field Documentation */}
          {fieldDoc && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>📋 Field Documentation</h3>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{
                    background: fieldDoc.status === 'submitted' ? '#10B98122' : '#F59E0B22',
                    color: fieldDoc.status === 'submitted' ? '#10B981' : '#F59E0B',
                    border: `1px solid ${fieldDoc.status === 'submitted' ? '#10B98144' : '#F59E0B44'}`,
                    borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700
                  }}>{fieldDoc.status === 'submitted' ? '✅ Submitted' : '⏳ In Progress'}</span>
                  {fieldDoc.pdf_url && (
                    <a href={fieldDoc.pdf_url} target="_blank" rel="noreferrer"
                      style={{ background: '#10B981', color: '#fff', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                      📄 Download Report
                    </a>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {fieldDoc.crew_count && <div><span style={{ color: '#4A6580', fontSize: 11 }}>CREW</span><br /><span style={{ color: '#fff', fontWeight: 600 }}>{fieldDoc.crew_count} workers</span></div>}
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>UNIFORMS</span><br /><span style={{ color: fieldDoc.uniforms_verified ? '#10B981' : '#EF4444', fontWeight: 600 }}>{fieldDoc.uniforms_verified ? '✅ Verified' : '❌ Not verified'}</span></div>
                {fieldDoc.dumpster_placed && <div><span style={{ color: '#4A6580', fontSize: 11 }}>DUMPSTERS</span><br /><span style={{ color: '#fff', fontWeight: 600 }}>{fieldDoc.dumpster_count || 1} on site</span></div>}
                {fieldDoc.equipment_used && <div><span style={{ color: '#4A6580', fontSize: 11 }}>EQUIPTER</span><br /><span style={{ color: '#fff', fontWeight: 600 }}>✅ Used</span></div>}
                <div><span style={{ color: '#4A6580', fontSize: 11 }}>COC</span><br /><span style={{ color: fieldDoc.coc_signed ? '#10B981' : '#F59E0B', fontWeight: 600 }}>{fieldDoc.coc_signed ? '✍️ Signed' : 'Pending'}</span></div>
                {fieldDoc.submitted_at && <div><span style={{ color: '#4A6580', fontSize: 11 }}>SUBMITTED</span><br /><span style={{ color: '#fff', fontWeight: 600 }}>{new Date(fieldDoc.submitted_at).toLocaleDateString()}</span></div>}
              </div>
              {fieldDoc.leftover_materials && <div style={{ marginTop: 12, color: '#F59E0B', fontSize: 13 }}>📦 Returns: {fieldDoc.leftover_materials}</div>}
              {/* Photo counts */}
              <div style={{ marginTop: 12, color: '#8FA8C0', fontSize: 12 }}>
                📷 {(fieldDoc.before_photos?.length || 0) + (fieldDoc.after_photos?.length || 0) + (fieldDoc.work_in_progress_photos?.length || 0)} photos
              </div>
            </div>
          )}



          {/* Notes & Tasks Tab */}
          {activeTab === 'notes' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
              {/* Notes Section */}
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259' }}>
                <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>💬 Notes</h3>
                
                {/* New Note Input */}
                <div style={{ marginBottom: 20, position: 'relative' }}>
                  <textarea
                    ref={noteInputRef}
                    value={newNoteContent}
                    onChange={e => {
                      const val = e.target.value;
                      const cursorPos = e.target.selectionStart;
                      setNewNoteContent(val);
                      
                      // Check for @ mention
                      const textBeforeCursor = val.slice(0, cursorPos);
                      const atMatch = textBeforeCursor.match(/@(\w*)$/);
                      if (atMatch) {
                        setShowMentionPicker(true);
                        setMentionFilter(atMatch[1].toLowerCase());
                        setMentionCursorPos(cursorPos);
                      } else {
                        setShowMentionPicker(false);
                      }
                    }}
                    onKeyDown={e => {
                      if (showMentionPicker && e.key === 'Escape') {
                        setShowMentionPicker(false);
                      }
                    }}
                    placeholder="Add a note... Type @ to mention someone"
                    style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 10, padding: 14, color: '#fff', fontSize: 14, minHeight: 80, resize: 'none' }}
                  />
                  
                  {/* Mention Picker Dropdown */}
                  {showMentionPicker && (
                    <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: '#243447', border: '1px solid #4A7EB5', borderRadius: 10, maxHeight: 200, overflowY: 'auto', marginBottom: 4, zIndex: 100 }}>
                      {allUsers
                        .filter(u => u.name.toLowerCase().includes(mentionFilter))
                        .slice(0, 6)
                        .map(user => (
                          <div
                            key={user.id}
                            onClick={() => {
                              // Find the @ position and replace with full mention
                              const textBeforeCursor = newNoteContent.slice(0, mentionCursorPos);
                              const atIndex = textBeforeCursor.lastIndexOf('@');
                              const newText = newNoteContent.slice(0, atIndex) + '@' + user.name + ' ' + newNoteContent.slice(mentionCursorPos);
                              setNewNoteContent(newText);
                              setShowMentionPicker(false);
                              noteInputRef.current?.focus();
                            }}
                            style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #2D4259' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#1E3448')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          >
                            <div style={{ width: 32, height: 32, borderRadius: 16, background: '#4A7EB5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                              {user.name.charAt(0)}
                            </div>
                            <div>
                              <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{user.name}</div>
                              <div style={{ color: '#4A6580', fontSize: 12 }}>{user.role}</div>
                            </div>
                          </div>
                        ))}
                      {allUsers.filter(u => u.name.toLowerCase().includes(mentionFilter)).length === 0 && (
                        <div style={{ padding: 14, color: '#4A6580', textAlign: 'center' }}>No matching team members</div>
                      )}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                    <span style={{ color: '#4A6580', fontSize: 12 }}>Type @ to mention team members</span>
                    <button
                      onClick={async () => {
                        if (!newNoteContent.trim()) return;
                        try {
                          const res = await api.post(`/notes/job/${id}`, { content: newNoteContent });
                          setJobNotes([res.data, ...jobNotes]);
                          setNewNoteContent('');
                        } catch { alert('Failed to add note'); }
                      }}
                      disabled={!newNoteContent.trim()}
                      style={{ background: newNoteContent.trim() ? '#4A7EB5' : '#2D4259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: newNoteContent.trim() ? 'pointer' : 'default' }}
                    >
                      Post Note
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                {jobNotes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#4A6580' }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>💭</div>
                    <div>No notes yet. Start the conversation!</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {jobNotes.map(note => (
                      <div key={note.id} style={{ background: note.pinned ? '#243447' : '#1E3448', borderRadius: 12, padding: 16, border: note.pinned ? '1px solid #F59E0B44' : '1px solid #2D4259' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 18, background: '#4A7EB5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                              {note.user?.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{note.user?.name || 'Unknown'}</div>
                              <div style={{ color: '#4A6580', fontSize: 11 }}>
                                {new Date(note.created_at).toLocaleString()}
                                {note.pinned && <span style={{ color: '#F59E0B', marginLeft: 8 }}>📌 Pinned</span>}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              await api.patch(`/notes/${note.id}`, { pinned: !note.pinned });
                              setJobNotes(jobNotes.map(n => n.id === note.id ? { ...n, pinned: !n.pinned } : n));
                            }}
                            style={{ background: 'transparent', border: 'none', color: note.pinned ? '#F59E0B' : '#4A6580', cursor: 'pointer', fontSize: 16 }}
                            title={note.pinned ? 'Unpin' : 'Pin'}
                          >
                            📌
                          </button>
                        </div>
                        <div style={{ color: '#E2E8F0', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {note.content.split(/(@\w+(?:\s+\w+)?)/g).map((part: string, i: number) =>
                            part.startsWith('@') ? <span key={i} style={{ color: '#4A7EB5', fontWeight: 600 }}>{part}</span> : part
                          )}
                        </div>
                        
                        {/* Replies */}
                        {note.replies?.length > 0 && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                            {note.replies.map((reply: any) => (
                              <div key={reply.id} style={{ display: 'flex', gap: 10, marginBottom: 12, paddingLeft: 20 }}>
                                <div style={{ width: 28, height: 28, borderRadius: 14, background: '#3D5A73', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 11 }}>
                                  {reply.user?.name?.charAt(0) || '?'}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: '#8FA8C0', fontSize: 12 }}><strong style={{ color: '#fff' }}>{reply.user?.name}</strong> · {new Date(reply.created_at).toLocaleString()}</div>
                                  <div style={{ color: '#C9D6E3', fontSize: 13, marginTop: 4 }}>{reply.content}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reply Input */}
                        {replyingTo === note.id ? (
                          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                            <input
                              value={replyContent}
                              onChange={e => setReplyContent(e.target.value)}
                              placeholder="Write a reply..."
                              style={{ flex: 1, background: '#1A2B3C', border: '1px solid #2D4259', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13 }}
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                if (!replyContent.trim()) return;
                                try {
                                  const res = await api.post(`/notes/job/${id}`, { content: replyContent, parent_id: note.id });
                                  setJobNotes(jobNotes.map(n => n.id === note.id ? { ...n, replies: [...(n.replies || []), res.data] } : n));
                                  setReplyContent('');
                                  setReplyingTo(null);
                                } catch { alert('Failed to reply'); }
                              }}
                              style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13 }}
                            >
                              Reply
                            </button>
                            <button
                              onClick={() => { setReplyingTo(null); setReplyContent(''); }}
                              style={{ background: '#2D4259', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
                            <button
                              onClick={() => setReplyingTo(note.id)}
                              style={{ background: 'transparent', border: 'none', color: '#4A7EB5', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                            >
                              ↩ Reply
                            </button>
                            <button
                              onClick={() => {
                                setNewTaskTitle(note.content.slice(0, 100) + (note.content.length > 100 ? '...' : ''));
                                setNewTaskDescription(`From note by ${note.user?.name}: ${note.content}`);
                                setNewTaskJobId(id || null);
                                setShowTaskModal(true);
                              }}
                              style={{ background: 'transparent', border: 'none', color: '#10B981', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                            >
                              ➕ Create Task
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tasks Sidebar */}
              <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', height: 'fit-content' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>✅ Tasks</h3>
                  <button
                    onClick={() => {
                      setNewTaskTitle('');
                      setNewTaskDescription('');
                      setNewTaskJobId(id || null);
                      setShowTaskModal(true);
                    }}
                    style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    + New
                  </button>
                </div>
                
                {jobTasks.filter(t => !t.completed_at).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 30, color: '#4A6580' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>✨</div>
                    <div style={{ fontSize: 13 }}>No open tasks</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {jobTasks.filter(t => !t.completed_at).map(task => (
                      <div
                        key={task.id}
                        style={{ background: '#243447', borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}
                      >
                        <button
                          onClick={async () => {
                            await api.post(`/tasks/${task.id}/complete`);
                            setJobTasks(jobTasks.map(t => t.id === task.id ? { ...t, completed_at: new Date().toISOString() } : t));
                          }}
                          style={{ width: 22, height: 22, borderRadius: 6, border: '2px solid #4A6580', background: 'transparent', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#fff', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
                          <div style={{ color: '#4A6580', fontSize: 11 }}>
                            {task.assigned_user?.name}
                            {task.due_at && ` · ${new Date(task.due_at).toLocaleDateString()}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Completed Tasks */}
                {jobTasks.filter(t => t.completed_at).length > 0 && (
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
                    <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, marginBottom: 10 }}>COMPLETED</div>
                    {jobTasks.filter(t => t.completed_at).slice(0, 5).map(task => (
                      <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', opacity: 0.6 }}>
                        <span style={{ color: '#10B981', fontSize: 14 }}>✓</span>
                        <span style={{ color: '#8FA8C0', fontSize: 12, textDecoration: 'line-through' }}>{task.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents (documents tab) */}
          {(isAC || activeTab === 'documents') && <>
          {/* Documents */}
          <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>📁 Documents</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={docCategory}
                  onChange={e => setDocCategory(e.target.value)}
                  style={{ background: '#243447', color: '#fff', border: '1px solid #2D4259', borderRadius: 6, padding: '5px 8px', fontSize: 12 }}
                >
                  <option value="document">📄 Document</option>
                  <option value="receipt">🧾 Receipt</option>
                  <option value="invoice">💰 Invoice</option>
                  <option value="work_order">🔧 Work Order</option>
                  <option value="signed_contract">✍️ Signed Contract</option>
                  <option value="insurance_estimate">🏦 Insurance Estimate</option>
                  <option value="supplement">📋 Supplement</option>
                  <option value="photo">📷 Photo</option>
                </select>
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={docDescription}
                  onChange={e => setDocDescription(e.target.value)}
                  style={{ background: '#243447', color: '#fff', border: '1px solid #2D4259', borderRadius: 6, padding: '5px 8px', fontSize: 12, width: 160 }}
                />
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" style={{ display: 'none' }} onChange={uploadDocument} accept="*/*" />
                  <span style={{ background: uploadingDoc ? '#2D4259' : '#4A7EB5', color: '#fff', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
                    {uploadingDoc ? 'Uploading...' : '+ Upload'}
                  </span>
                </label>
              </div>
            </div>
            {documents.length === 0 ? (
              <div style={{ color: '#4A6580', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No documents uploaded yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {documents.map((doc: any) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#243447', borderRadius: 10, border: '1px solid #2D4259' }}>
                    <span style={{ fontSize: 22 }}>{getFileIcon(doc.mime_type || doc.file_type)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.file_name || doc.name}</div>
                      {doc.estimate_parse_details?.reason && <div style={{color:'#F59E0B',fontSize:12,whiteSpace:'normal'}}>{doc.estimate_parse_details.reason}</div>}
                      {doc.description && <div style={{ color: '#8BA7C0', fontSize: 12, marginTop: 1 }}>{doc.description}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        {(() => {
                          const CAT_COLORS: Record<string, { bg: string; label: string }> = {
                            document: { bg: '#3B82F6', label: 'Document' },
                            receipt: { bg: '#F97316', label: 'Receipt' },
                            invoice: { bg: '#10B981', label: 'Invoice' },
                            work_order: { bg: '#F59E0B', label: 'Work Order' },
                            signed_contract: { bg: '#8B5CF6', label: 'Contract' },
                            insurance_estimate: { bg: '#EAB308', label: 'Ins. Estimate' },
                            supplement: { bg: '#EF4444', label: 'Supplement' },
                            photo: { bg: '#14B8A6', label: 'Photo' },
                          };
                          const cat = CAT_COLORS[doc.category] || { bg: '#4A6580', label: doc.category };
                          return <span style={{ background: cat.bg, color: '#fff', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{cat.label}</span>;
                        })()}
                        <span style={{ color: '#4A6580', fontSize: 11 }}>
                          {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB · ` : ''}{new Date(doc.created_at).toLocaleDateString()} · {doc.users?.name || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    <a href={doc.file_url || doc.url} target="_blank" rel="noreferrer" style={{ color: '#4A7EB5', fontSize: 13, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>View</a>
                    <button onClick={() => deleteDocument(doc.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>🗑</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          </>
          }

          {/* Stage History (history tab) */}
          {(isAC || activeTab === 'history') && <>
          {/* Stage History */}
          <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #2D4259', marginBottom: 20 }}>
            <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>History</h3>
            {history.map((item, i) => (
              <div key={item.id} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 2, background: i === history.length - 1 ? '#4A7EB5' : '#2D4259', borderRadius: 1, flexShrink: 0 }} />
                <div>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{item.stage_name}</div>
                  <div style={{ color: '#4A6580', fontSize: 12, marginTop: 2 }}>
                    {new Date(item.changed_at).toLocaleString()} {item.users?.name ? `· ${item.users.name}` : ''}
                  </div>
                  {item.notes && <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 2, fontStyle: 'italic' }}>{item.notes}</div>}
                </div>
              </div>
            ))}
          </div>

          </>
          }

          {/* Danger Zone — always visible */}
          {user?.role === 'admin' && (
            <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: '1px solid #EF444433', marginTop: 24, marginBottom: 20 }}>
              <h3 style={{ color: '#EF4444', fontWeight: 700, margin: '0 0 12px 0' }}>⚠️ Danger Zone</h3>
              <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 16, marginTop: 8 }}>Permanently delete this job and all associated data including stage history and commissions. This cannot be undone.</p>
              <button
                onClick={deleteJob}
                disabled={deleting}
                style={{ background: '#EF444422', border: '1px solid #EF444444', color: '#EF4444', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : '🗑 Delete Job'}
              </button>
            </div>
          )}
        </div>

        {/* Right column — Stage */}
        <div>
          <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 20, border: `1px solid ${stageColor}44`, position: 'sticky', top: 20 }}>
            <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Current Stage</div>
            <div style={{ color: stageColor, fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{job.pipeline_stage_name}</div>
            <div style={{ color: '#4A6580', fontSize: 12, marginBottom: 16 }}>{isLost ? 'Retained in Lost' : `${currentIdx + 1} of ${activeStageCount}`}</div>

            {/* Progress */}
            <div style={{ background: '#243447', borderRadius: 4, height: 6, marginBottom: 20, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: stageColor, borderRadius: 4, transition: 'width 0.5s' }} />
            </div>


            {nextStage && (user?.role === 'specialist' || user?.role === 'admin' || user?.role === 'pm') && (
              <button
                onClick={advanceStage}
                disabled={advancing}
                style={{ width: '100%', padding: '12px', background: '#4A7EB5', color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14, marginBottom: 8 }}
              >
                {advancing ? 'Processing...' : `→ ${nextStage.name}`}

              </button>
            )}

            {/* Close Job — AC jobs at Commission Paid, admin only */}
            {isAC && job.pipeline_stage_name === 'Commission Paid' && user?.role === 'admin' && (
              <button
                onClick={async () => {
                  if (!window.confirm('Close this job? This marks it as fully complete and archived.')) return;
                  try {
                    await api.post(`/jobs/${id}/close`);
                    fetchAll();
                  } catch (e: any) {
                    alert('Failed to close job: ' + (e?.response?.data?.error || e.message));
                  }
                }}
                style={{ width: '100%', padding: '12px', background: '#4A658022', border: '1px solid #4A6580', color: '#4A6580', borderRadius: 10, fontWeight: 700, fontSize: 14, marginBottom: 8, cursor: 'pointer' }}
              >
                ✓ Close Job
              </button>
            )}

            {/* Fully closed banner */}
            {isAC && job.pipeline_stage_name === 'Closed' && (
              <div style={{ background: '#4A658022', border: '1px solid #4A658044', borderRadius: 10, padding: '10px 14px', textAlign: 'center', marginBottom: 8 }}>
                <div style={{ color: '#4A6580', fontWeight: 800, fontSize: 14 }}>✅ Job Closed</div>
                <div style={{ color: '#4A6580', fontSize: 12, marginTop: 2 }}>Fully complete and archived</div>
              </div>
            )}

            {/* All stages list */}
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#4A6580', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>All Stages</div>
              {stages.map((s, i) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: !isLost && i < currentIdx ? '#10B981' : i === currentIdx ? stageColor : '#2D4259', flexShrink: 0 }} />
                  <span style={{ color: i === currentIdx ? stageColor : !isLost && i < currentIdx ? '#10B981' : '#4A6580', fontSize: 12, fontWeight: i === currentIdx ? 700 : 400 }}>
                    {s.name}{financials?.payment_arrangement === 'retail' && INSURANCE_ONLY_STAGES.includes(s.name) ? ' — Not applicable' : ''}

                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #2D4259' }}>
            <JobDisposition initiallyOpen={dispositionRequested} job={job} onSaved={fetchAll} />
            </div>
          </div>
        </div>
      </div>

    </div>

    {/* Edit Modal */}
    {showEditModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 28, width: 500, border: '1px solid #2D4259', maxHeight: '90vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ color: '#fff', fontWeight: 800, margin: 0 }}>Edit Job Details</h2>
            <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', color: '#8FA8C0', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>

          {/* First / Last Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>First Name *</label>
              <input
                value={editForm.first_name || ''}
                onChange={e => setEditForm((prev: any) => ({ ...prev, first_name: e.target.value }))}
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Last Name *</label>
              <input
                value={editForm.last_name || ''}
                onChange={e => setEditForm((prev: any) => ({ ...prev, last_name: e.target.value }))}
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Other fields */}
          {[  
            { label: 'Address', key: 'address' },
            { label: 'Phone', key: 'homeowner_phone' },
            { label: 'Email', key: 'homeowner_email' },
            { label: 'Insurance Company', key: 'insurance_company' },
            { label: 'Claim Number', key: 'claim_number' },
          ].map(({ label, key }) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>{label}</label>
              <input
                value={editForm[key] || ''}
                onChange={e => setEditForm((prev: any) => ({ ...prev, [key]: e.target.value }))}
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              onClick={saveEdit}
              disabled={saving}
              style={{ flex: 1, background: '#4A7EB5', color: '#fff', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={() => setShowEditModal(false)}
              style={{ background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 10, padding: '12px 20px', fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Task Creation Modal */}
    {showTaskModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
        <div style={{ background: '#1E3448', borderRadius: 20, padding: 24, width: '100%', maxWidth: 500, border: '1px solid #2D4259' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ color: '#fff', margin: 0, fontWeight: 700 }}>📋 New Task</h2>
            <button onClick={() => setShowTaskModal(false)} style={{ background: 'none', border: 'none', color: '#8FA8C0', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Title *</label>
              <input
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
                placeholder="Task title..."
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
              />
            </div>
            
            <div>
              <label style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Description</label>
              <textarea
                value={newTaskDescription}
                onChange={e => setNewTaskDescription(e.target.value)}
                placeholder="Task details..."
                rows={3}
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, resize: 'vertical' }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Assign To *</label>
                <select
                  value={newTaskAssignee}
                  onChange={e => setNewTaskAssignee(e.target.value)}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
                >
                  <option value="">Select user...</option>
                  {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              
              <div>
                <label style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Due Date</label>
                <input
                  type="datetime-local"
                  value={newTaskDue}
                  onChange={e => setNewTaskDue(e.target.value)}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Type</label>
                <select
                  value={newTaskType}
                  onChange={e => setNewTaskType(e.target.value)}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
                >
                  <option value="follow_up">Follow Up</option>
                  <option value="call">Phone Call</option>
                  <option value="document">Document</option>
                  <option value="inspection">Inspection</option>
                  <option value="schedule">Schedule</option>
                  <option value="approval">Approval</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Priority</label>
                <select
                  value={newTaskPriority}
                  onChange={e => setNewTaskPriority(e.target.value)}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
                >
                  <option value="low">🟢 Low</option>
                  <option value="normal">🟡 Normal</option>
                  <option value="high">🟠 High</option>
                  <option value="urgent">🔴 Urgent</option>
                </select>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button
              onClick={async () => {
                if (!newTaskTitle.trim() || !newTaskAssignee) {
                  alert('Title and assignee are required');
                  return;
                }
                setCreatingTask(true);
                try {
                  const res = await api.post('/tasks', {
                    title: newTaskTitle,
                    description: newTaskDescription || null,
                    assigned_to: newTaskAssignee,
                    job_id: newTaskJobId,
                    due_at: newTaskDue || null,
                    task_type: newTaskType,
                    priority: newTaskPriority,
                  });
                  setJobTasks([res.data, ...jobTasks]);
                  setShowTaskModal(false);
                  setNewTaskTitle('');
                  setNewTaskDescription('');
                  setNewTaskAssignee('');
                  setNewTaskDue('');
                  setNewTaskPriority('normal');
                  setNewTaskType('follow_up');
                } catch (err) {
                  alert('Failed to create task');
                } finally {
                  setCreatingTask(false);
                }
              }}
              disabled={creatingTask}
              style={{ flex: 1, background: '#10B981', color: '#fff', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none', opacity: creatingTask ? 0.6 : 1 }}
            >
              {creatingTask ? 'Creating...' : 'Create Task'}
            </button>
            <button
              onClick={() => setShowTaskModal(false)}
              style={{ background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', borderRadius: 10, padding: '12px 20px', fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
